import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { motion, AnimatePresence, MotionConfig } from "framer-motion";
import useTypewriter from "./useTypewriter";
import UserContext from "./UserContext";
import { useReactToPrint } from "react-to-print";
import { PrintableLabReport } from "./LabReportPDF";
import TrendsPanel from "./TrendsPanel";
import SendIcon from "@mui/icons-material/Send";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import HistoryIcon from "@mui/icons-material/History";
import SaveIcon from "@mui/icons-material/Save";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import HomeIcon from "@mui/icons-material/Home";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import {
  Box,
  Button,
  Typography,
  TextField,
  Stack,
  Switch,
  Select,
  MenuItem,
  ListSubheader,
  IconButton,
  Tooltip,
  CircularProgress,
  useMediaQuery,
  Alert,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ThemeToggle from "./ThemeToggle";
import NavUserMenu from "./NavUserMenu";
import { formatEST } from "../dateUtils";
import { extractErrorDetail } from "./validators";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
// "My Results" ecosystem plan — dual input mode. When "true", this page
// shows Upload PDF + Browse My Tests; when "false" (default), only
// Upload PDF, unchanged from before this feature existed.
const BROWSE_MY_TESTS_ENABLED = process.env.REACT_APP_BROWSE_MY_TESTS_ENABLED === "true";

const BRAND = {
  navy:      "#00357a",
  navyDark:  "#001f4d",
  gold:      "#c9a96e",
  navyLight: "#5b9bd5",
  offWhite:  "#f8f6f2",
  textDark:  "#0d1b2e",
  textMid:   "#4a5568",
};

const LANGUAGE_OPTIONS = [
  { value: "english",    label: "English",    lang: "en-US", voiceHint: "Google US English" },
  { value: "spanish",    label: "Español",    lang: "es-ES", voiceHint: "Google español" },
  { value: "french",     label: "Français",   lang: "fr-FR", voiceHint: "Google français" },
  { value: "german",     label: "Deutsch",    lang: "de-DE", voiceHint: "Google Deutsch" },
  { value: "portuguese", label: "Português",  lang: "pt-PT", voiceHint: "Google português" },
  { value: "chinese",    label: "中文",        lang: "zh-CN", voiceHint: "Google 普通话" },
  { value: "japanese",   label: "日本語",      lang: "ja-JP", voiceHint: "Google 日本語" },
  { value: "korean",     label: "한국어",      lang: "ko-KR", voiceHint: "Google 한국의" },
  { value: "hindi",      label: "हिन्दी",    lang: "hi-IN", voiceHint: "Google हिन्दी" },
  { value: "arabic",     label: "العربية",   lang: "ar-SA", voiceHint: "Google العربية" },
  { value: "russian",    label: "Русский",    lang: "ru-RU", voiceHint: "Google русский" },
  { value: "telugu",     label: "తెలుగు",    lang: "te-IN", voiceHint: "" },
  { value: "bengali",    label: "বাংলা",      lang: "bn-IN", voiceHint: "" },
  { value: "urdu",       label: "اردو",       lang: "ur-PK", voiceHint: "" },
  { value: "farsi",      label: "فارسی",      lang: "fa-IR", voiceHint: "" },
  { value: "vietnamese", label: "Tiếng Việt", lang: "vi-VN", voiceHint: "Google tiếng Việt" },
];

// ── Part 6c — report-type dropdown (optional) ──────────────────────────────
// Proove-family options: fixed 3 catalog test_ids, labels copied VERBATIM
// from the backend's own _PROOVE_BOT_REPORT_TYPES dict (main.py, Part 6a) —
// confirmed by reading that dict directly, not guessed. These 3 are a
// closed, stable set by explicit product decision (same 3 ids
// _PROOVE_TEST_ID_TO_PAYMENT_ITEM gates payment links on), so hardcoding
// them here carries none of the drift risk the Labarium group below does.
const PROOVE_REPORT_TYPE_OPTIONS = [
  { value: "proove_opioid_risk", label: "Opioid Risk Profile" },
  { value: "proove_pain_perception", label: "Pain Perception Profile" },
  { value: "proove_pgx", label: "Drug Metabolism (PGx) Profile" },
];
const PROOVE_REPORT_TYPE_IDS = new Set(PROOVE_REPORT_TYPE_OPTIONS.map((o) => o.value));

// 2026-10-15 fix (user-directed correction) — Labarium's own (non-Proove)
// report types were originally modeled as 2 families ("toxicology"/
// "infectious_disease_molecular"), each assumed to cover multiple seed-
// catalog sub-panels (hence the prior version of this file hardcoding 4
// placeholder test_ids with a flagged drift risk). Corrected per explicit
// instruction: these mirror real requisition types directly, ONE report
// per category — Blood, Tox, Molecular — the same flat structure as
// PROOVE_REPORT_TYPE_OPTIONS above (3 fixed types, no sub-panels). These
// 3 values are DELIBERATELY the same fixed set as backend main.py's own
// `_REPORT_FAMILY_ALLOWLIST` (search for it there) — both sides are small,
// fixed constants (not a growing catalog), so hardcoding here carries the
// same low drift risk as PROOVE_REPORT_TYPE_OPTIONS already does, not the
// catalog-drift risk the old 4-placeholder version had. If a 4th family is
// ever added, update both this list and that allowlist together.
const LABARIUM_REPORT_TYPE_OPTIONS = [
  { value: "blood", label: "Blood Panel" },
  { value: "tox", label: "Toxicology Screen" },
  { value: "molecular", label: "Molecular / PCR Panel" },
];
const LABARIUM_REPORT_TYPE_TO_FAMILY = Object.fromEntries(
  LABARIUM_REPORT_TYPE_OPTIONS.map((o) => [o.value, o.value])
);

// Converts Part 6a's server-sanitized clinical_summary_html/disclaimer HTML
// (allowlisted to EXACTLY <b>/<ul>/<li> server-side — confirmed by reading
// _TypedHtmlSanitizer._ALLOWED_TAGS in main.py directly, not assumed) into
// the same Markdown syntax this file's existing ReactMarkdown path already
// renders everywhere else. Deliberate choice over adding
// dangerouslySetInnerHTML to this file: this file renders ALL other AI
// output (the existing /ask/*_bot answer, every follow-up) through
// ReactMarkdown, and introducing a second, raw-HTML rendering mechanism
// alongside it — even for server-sanitized content — is a drift/consistency
// risk this reuse avoids. A blank line is inserted before each list so a
// preceding "**Heading**" line doesn't run into the first list item (plain
// string concatenation of <b>Heading</b><ul>... would otherwise collapse
// onto one line). HTML character references (&amp; etc.) are left as-is —
// CommonMark/remark decode entity references in plain text the same way an
// HTML parser would, so no separate unescaping step is needed here.
function typedHtmlToMarkdown(html) {
  if (!html) return "";
  // Per-tag (not paired-capture) replacement — deliberate. A paired regex
  // like <li>(.*?)<\/li> silently drops content on an unclosed tag or a
  // nested list, and this HTML is NOT guaranteed to be the clean, flat
  // <b>/<ul>/<li>-only structure the backend's own allowlist comment
  // claims: _select_patient_advocacy_resources (main.py) appends
  // '<p><b>Patient Support Resources</b></p><ul>...' to clinical_summary_html
  // AFTER _typed_sanitize_summary_html already ran (confirmed by reading
  // _typed_vertex_summarize directly), so a real <p> tag reaches this
  // function despite the allowlist's own doc claiming only 3 tags ever
  // appear. Per-tag replacement + a final generic-tag strip handles that
  // (and any other future addition) without silently losing text.
  let s = html;
  s = s.replace(/<p>/g, "\n\n").replace(/<\/p>/g, "\n\n");
  s = s.replace(/<ul>/g, "\n\n").replace(/<\/ul>/g, "\n\n");
  s = s.replace(/<li>/g, "\n- ").replace(/<\/li>/g, "\n");
  s = s.replace(/<b>/g, "**").replace(/<\/b>/g, "**");
  // Defensive fallback for any other/unexpected tag — display-only, strips
  // the tag but keeps its inner text (same "drop wrapper, keep text"
  // discipline the backend's own sanitizer uses for disallowed tags).
  s = s.replace(/<\/?[a-zA-Z][^>]*>/g, "");
  s = s.replace(/\n{3,}/g, "\n\n"); // collapse runs into one blank-line separator
  return s.trim();
}

const PHASES = [
  { key: "uploading",   label: "Uploading & converting pages…",         pct: 20 },
  { key: "processing",  label: "Processing report layout…",             pct: 45 },
  { key: "analyzing",   label: "AI is reading every result…",           pct: 75 },
  { key: "summarizing", label: "Generating your personalised summary…", pct: 92 },
  // ui-ux-checker finding (2026-10-21) — Browse My Tests used to reuse
  // "analyzing"/"summarizing" from the upload phases above, which
  // describe AI work that genuinely never happens for a browse-select
  // (no OCR, no vision call — the summary is already stored). A
  // dedicated phase with its own honest copy instead.
  { key: "loadingStored", label: "Loading your saved report…", pct: 70 },
];

// ui-ux-checker finding — the picker rows used to mechanically convert
// report_type_id to Title Case ("proove_opioid_risk" -> "Proove Opioid
// Risk"), leaking the internal "proove_" prefix instead of reusing the
// same human labels PROOVE_REPORT_TYPE_OPTIONS/LABARIUM_REPORT_TYPE_
// OPTIONS already define for the "Report type" Select right above this
// list — two adjacent controls showing different vocabulary for the
// same values. Native report_type_id values ("blood_panel" etc.) don't
// share a value-string with LABARIUM_REPORT_TYPE_OPTIONS (that list's
// own values are report_family strings, "blood"/"tox"/"molecular" — a
// different, coarser id), so those four get their own direct entries
// here rather than a mapping through that list.
const _BROWSE_REPORT_TYPE_LABELS = {
  ...Object.fromEntries(PROOVE_REPORT_TYPE_OPTIONS.map((o) => [o.value, o.label])),
  blood_panel: "Blood Panel",
  tox_panel: "Toxicology Screen",
  pcr_panel: "Molecular / PCR Panel",
  drug_screen: "Drug Screen",
};
function browseReportTypeLabel(rtid) {
  return _BROWSE_REPORT_TYPE_LABELS[rtid] || String(rtid || "").replace(/_/g, " ");
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function loadVoices() {
  return new Promise((resolve) => {
    const voices = window.speechSynthesis.getVoices();
    if (voices.length) { resolve(voices); return; }
    const onChanged = () => {
      window.speechSynthesis.removeEventListener("voiceschanged", onChanged);
      resolve(window.speechSynthesis.getVoices());
    };
    window.speechSynthesis.addEventListener("voiceschanged", onChanged);
    setTimeout(() => resolve(window.speechSynthesis.getVoices()), 3000);
  });
}

function chunkText(text, maxChars = 200) {
  const sentences = text.replace(/([.!?])\s+/g, "$1\n").split("\n");
  const chunks = [];
  let current = "";
  for (const s of sentences) {
    if ((current + s).length > maxChars) {
      if (current) chunks.push(current.trim());
      current = s + " ";
    } else {
      current += s + " ";
    }
  }
  if (current.trim()) chunks.push(current.trim());
  return chunks;
}

function speakChunks(chunks, voice, lang, rate, onEnd) {
  if (!chunks.length) { onEnd(); return; }
  const [head, ...tail] = chunks;
  const utt = new SpeechSynthesisUtterance(head);
  if (voice) { utt.voice = voice; utt.lang = voice.lang; }
  else { utt.lang = lang; }
  utt.rate = rate;
  utt.onend  = () => speakChunks(tail, voice, lang, rate, onEnd);
  utt.onerror = () => speakChunks(tail, voice, lang, rate, onEnd);
  window.speechSynthesis.speak(utt);
}

function detectStatus(children) {
  const text = extractText(children);
  const hasHigh = /\bhigh\b|↑/i.test(text);
  const hasLow  = /\blow\b|↓/i.test(text);
  if (hasHigh && hasLow) return null;
  if (hasHigh) return "high";
  if (hasLow)  return "low";
  if (/\bcritical\b/i.test(text)) return "critical";
  if (/\bnormal\b/i.test(text))   return "normal";
  return null;
}

function extractText(node) {
  if (!node) return "";
  if (typeof node === "string") return node;
  if (Array.isArray(node)) return node.map(extractText).join("");
  if (node?.props?.children) return extractText(node.props.children);
  return "";
}

// ─────────────────────────────────────────────────────────────────────────────
//  ANIMATED TOAST SYSTEM
// ─────────────────────────────────────────────────────────────────────────────
const TOAST_CONFIG = {
  success: {
    icon: <CheckCircleIcon sx={{ fontSize: 17 }} />,
    accent: "#22c55e",
    border: "rgba(34,197,94,0.28)",
    bg: "rgba(34,197,94,0.08)",
  },
  error: {
    icon: <ErrorIcon sx={{ fontSize: 17 }} />,
    accent: "#ef4444",
    border: "rgba(239,68,68,0.28)",
    bg: "rgba(239,68,68,0.08)",
  },
  warning: {
    icon: <WarningAmberIcon sx={{ fontSize: 17 }} />,
    accent: "#f59e0b",
    border: "rgba(245,158,11,0.28)",
    bg: "rgba(245,158,11,0.08)",
  },
};

function ToastItem({ toast, onRemove, isDark }) {
  const cfg = TOAST_CONFIG[toast.type] || TOAST_CONFIG.success;
  const duration = toast.duration ?? 5000;
  // 2026-10-16 (ui-ux-checker-flagged perf finding) — this used to drive a
  // per-frame setState/requestAnimationFrame loop animating `width`
  // (layout/paint every frame). Replaced with a single CSS transition on
  // transform:scaleX (compositor-only) triggered once on mount.
  const [shrink, setShrink] = useState(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setShrink(true));
    const timeout = setTimeout(() => onRemove(toast.id), duration);
    return () => { cancelAnimationFrame(raf); clearTimeout(timeout); };
  }, [toast.id, duration, onRemove]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 60, scale: 0.9 }}
      animate={{ opacity: 1, x: 0,  scale: 1 }}
      exit={{ opacity: 0, x: 60, scale: 0.88, transition: { duration: 0.18 } }}
      transition={{ type: "spring", stiffness: 450, damping: 34 }}
      style={{ position: "relative" }}
    >
      <Box sx={{
        display: "flex", alignItems: "flex-start", gap: 1.25,
        minWidth: 290, maxWidth: 400,
        bgcolor: isDark ? "rgba(15,23,42,0.94)" : "rgba(255,255,255,0.97)",
        backdropFilter: "blur(24px) saturate(1.5)",
        border: `1px solid ${cfg.border}`,
        borderRadius: "14px",
        px: 1.75, pt: 1.4, pb: 1.8,
        boxShadow: isDark
          ? "0 12px 40px rgba(0,0,0,0.45), 0 1px 0 rgba(255,255,255,0.04)"
          : "0 12px 40px rgba(0,53,122,0.14), 0 1px 0 rgba(255,255,255,1)",
        overflow: "hidden",
        position: "relative",
      }}>
        <Box sx={{ color: cfg.accent, flexShrink: 0, mt: "2px" }}>{cfg.icon}</Box>
        <Typography sx={{
          flex: 1, fontSize: 14, lineHeight: 1.55, fontWeight: 500,
          color: isDark ? "#e2e8f0" : "#1e293b",
        }}>
          {toast.message}
        </Typography>
        <motion.div whileHover={{ scale: 1.15 }} whileTap={{ scale: 0.9 }}>
          <IconButton size="small" onClick={() => onRemove(toast.id)} sx={{
            p: 0.3, mt: "-2px", color: isDark ? "#475569" : "#94a3b8",
            "&:hover": { color: isDark ? "#cbd5e1" : "#475569", bgcolor: "transparent" },
          }}>
            <CloseRoundedIcon sx={{ fontSize: 14.5 }} />
          </IconButton>
        </motion.div>
        {/* Progress bar */}
        <Box sx={{
          position: "absolute", bottom: 0, left: 0, right: 0, height: 2,
          bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
          overflow: "hidden",
        }}>
          <Box sx={{
            height: "100%", width: "100%", bgcolor: cfg.accent,
            transformOrigin: "left",
            transform: shrink ? "scaleX(0)" : "scaleX(1)",
            transition: `transform ${duration}ms linear`,
            borderRadius: "0 0 2px 2px",
            opacity: 0.7,
          }} />
        </Box>
      </Box>
    </motion.div>
  );
}

function ToastContainer({ toasts, onRemove, isDark }) {
  return (
    <Box sx={{
      position: "fixed", bottom: 24, right: 24, zIndex: 9999,
      display: "flex", flexDirection: "column-reverse", gap: 1,
      pointerEvents: "none",
      "& > *": { pointerEvents: "auto" },
    }}>
      <AnimatePresence mode="popLayout">
        {toasts.map(t => (
          <ToastItem key={t.id} toast={t} onRemove={onRemove} isDark={isDark} />
        ))}
      </AnimatePresence>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  CONFIRM MODAL
// ─────────────────────────────────────────────────────────────────────────────
function ConfirmModal({ open, onConfirm, onCancel, isDark }) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{
              position: "fixed", inset: 0, zIndex: 1400,
              background: "rgba(2,6,23,0.6)",
              backdropFilter: "blur(12px)",
            }}
            onClick={onCancel}
          />
          <motion.div
            key="card"
            initial={{ opacity: 0, scale: 0.84, y: 32 }}
            animate={{ opacity: 1, scale: 1,    y: 0 }}
            exit={{   opacity: 0, scale: 0.88,  y: 20, transition: { duration: 0.18 } }}
            transition={{ type: "spring", stiffness: 400, damping: 28 }}
            style={{
              position: "fixed", inset: 0, zIndex: 1401,
              display: "flex", alignItems: "center", justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <Box sx={{
              pointerEvents: "auto",
              bgcolor: isDark ? "rgba(10,18,40,0.97)" : "rgba(255,255,255,0.98)",
              backdropFilter: "blur(32px) saturate(1.6)",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.09)" : "rgba(0,53,122,0.1)"}`,
              borderRadius: "22px",
              p: "28px 30px 26px",
              maxWidth: 400, width: "calc(100vw - 48px)",
              boxShadow: isDark
                ? "0 48px 120px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.05), inset 0 1px 0 rgba(255,255,255,0.06)"
                : "0 48px 120px rgba(0,53,122,0.22), inset 0 1px 0 rgba(255,255,255,1)",
            }}>
              {/* Icon badge */}
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.08, type: "spring", stiffness: 500, damping: 28 }}
              >
                <Box sx={{
                  width: 52, height: 52, borderRadius: "16px", mb: 2.5,
                  background: "linear-gradient(135deg, rgba(239,68,68,0.15), rgba(220,38,38,0.08))",
                  border: "1px solid rgba(239,68,68,0.25)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <DeleteSweepIcon sx={{ color: "#ef4444", fontSize: 26 }} />
                </Box>
              </motion.div>

              <Typography sx={{
                fontSize: 17.5, fontWeight: 800,
                color: isDark ? "#f1f5f9" : "#0d1b2e",
                letterSpacing: "-0.01em", mb: 1,
              }}>
                Clear all saved memory?
              </Typography>
              <Typography sx={{
                fontSize: 15, color: isDark ? "#94a3b8" : "#475569",
                lineHeight: 1.72, mb: 3.5,
              }}>
                All saved analyses for this assistant will be permanently deleted.
                Trend detection resets to zero.{" "}
                <Box component="span" sx={{ fontWeight: 700, color: "#ef4444" }}>
                  This cannot be undone.
                </Box>
              </Typography>

              <Stack direction="row" spacing={1.25}>
                <motion.div style={{ flex: 1 }} whileTap={{ scale: 0.97 }}>
                  <Button fullWidth onClick={onCancel} sx={{
                    borderRadius: "12px", fontSize: 15, fontWeight: 600, py: 1.2,
                    color: isDark ? "#94a3b8" : "#64748b",
                    bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
                    border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`,
                    "&:hover": { bgcolor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.07)" },
                    transition: "all 0.18s",
                  }}>
                    Cancel
                  </Button>
                </motion.div>
                <motion.div style={{ flex: 1 }} whileTap={{ scale: 0.97 }}>
                  <Button fullWidth onClick={onConfirm} variant="contained" sx={{
                    borderRadius: "12px", fontSize: 15, fontWeight: 700, py: 1.2,
                    background: "linear-gradient(135deg, #ef4444, #dc2626)",
                    boxShadow: "0 4px 20px rgba(239,68,68,0.4)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #f87171, #ef4444)",
                      boxShadow: "0 8px 28px rgba(239,68,68,0.5)",
                      transform: "translateY(-1px)",
                    },
                    transition: "all 0.2s",
                  }}>
                    Clear Memory
                  </Button>
                </motion.div>
              </Stack>
            </Box>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  MARKDOWN COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────
const markdownComponents = (isDark, textPrimary, textSecondary, border, BRAND) => ({
  h1: ({ children }) => (
    <Box sx={{
      bgcolor: BRAND.navy, color: "#fff", px: 2.5, py: 1.5,
      borderRadius: 1, mt: 3.5, mb: 2,
      fontWeight: 800, fontSize: 20, letterSpacing: "0.01em",
    }}>
      {children}
    </Box>
  ),
  h2: ({ children }) => (
    <Box sx={{
      display: "flex", alignItems: "stretch",
      mt: 3, mb: 1.5, borderRadius: "0 6px 6px 0",
      overflow: "hidden",
      bgcolor: isDark ? "#1e3a5f" : "#edf2fc",
      border: `1px solid ${isDark ? "#2d4f7c" : "#d0ddf5"}`,
    }}>
      <Box sx={{ width: 4, flexShrink: 0, bgcolor: BRAND.gold }} />
      <Typography sx={{
        px: 2, py: 1.25,
        fontWeight: 700, fontSize: 17,
        color: isDark ? "#93c5fd" : BRAND.navy,
        lineHeight: 1.4,
      }}>
        {children}
      </Typography>
    </Box>
  ),
  h3: ({ children }) => (
    <Typography sx={{
      fontWeight: 700, fontSize: 15,
      color: isDark ? "#cbd5e1" : BRAND.navy,
      mt: 2.5, mb: 0.75,
      borderBottom: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
      pb: 0.5,
    }}>
      {children}
    </Typography>
  ),
  p: ({ children }) => (
    <Typography component="p" sx={{
      mb: 1.5, lineHeight: 1.9, fontSize: 15, color: textPrimary,
    }}>
      {children}
    </Typography>
  ),
  ul: ({ children }) => (
    <Box component="ul" sx={{
      pl: 2, mb: 1,
      listStyleType: "none",
      "& li::marker": { display: "none" },
    }}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={{ pl: 2.5, mb: 1 }}>
      {children}
    </Box>
  ),
  li: ({ children, ordered, index }) => {
    const status = detectStatus(children);
    const bulletColor = {
      normal:   "#16a34a",
      high:     "#dc2626",
      low:      "#b47800",
      critical: "#9333ea",
    }[status] || BRAND.navy;
    const bullet = ordered ? `${(index ?? 0) + 1}.` : "•";
    return (
      <Box component="li" sx={{
        display: "flex", alignItems: "flex-start",
        gap: 1.25, mb: 0.9, listStyle: "none",
      }}>
        <Box component="span" sx={{
          flexShrink: 0, mt: "3px", minWidth: 16,
          fontWeight: 700, fontSize: 15,
          color: bulletColor, lineHeight: 1.65,
        }}>
          {bullet}
        </Box>
        <Box sx={{ flex: 1, fontSize: 15, color: textPrimary, lineHeight: 1.85 }}>
          {children}
        </Box>
      </Box>
    );
  },
  strong: ({ children }) => (
    <Box component="strong" sx={{ fontWeight: 700, color: isDark ? "#f1f5f9" : BRAND.navy }}>
      {children}
    </Box>
  ),
  em: ({ children }) => (
    <Box component="em" sx={{ color: textSecondary, fontStyle: "italic" }}>
      {children}
    </Box>
  ),
  code: ({ children, isInline }) => (
    <Box
      component={isInline ? "code" : "pre"}
      sx={{
        fontFamily: "monospace", fontSize: 14.5,
        px: 0.75, py: 0.2, borderRadius: 0.5,
        bgcolor: isDark ? "#0f172a" : "#f1f5f9",
        color: isDark ? "#7dd3fc" : BRAND.navy,
        border: `1px solid ${border}`,
        display: isInline ? "inline" : "block",
        whiteSpace: isInline ? "normal" : "pre-wrap",
        my: isInline ? 0 : 1,
      }}
    >
      {children}
    </Box>
  ),
  hr: () => (
    <Box sx={{
      my: 3, height: "1px", border: "none",
      background: `linear-gradient(to right, ${BRAND.gold}, transparent)`,
    }} />
  ),
  blockquote: ({ children }) => (
    <Box component="blockquote" sx={{
      borderLeft: `4px solid ${BRAND.gold}`,
      pl: 2, my: 2,
      bgcolor: isDark ? "#0f172a" : "#fdf8f3",
      py: 1.5, pr: 2,
      borderRadius: "0 8px 8px 0",
      "& p": { color: textSecondary, fontSize: 14, mb: 0, lineHeight: 1.75 },
    }}>
      {children}
    </Box>
  ),
  // 2026-10-16 (security-auditor-flagged real gap) — AI-generated content
  // can contain adversarial Markdown (indirect prompt injection from a
  // malicious uploaded report instructing the model to embed a tracking
  // pixel). A real <img> would auto-fetch its src with no user interaction
  // and no CSP in this app to block the request, silently exfiltrating
  // whatever the model embedded in the URL. Never render a real img tag —
  // alt text only, no network fetch, ever.
  img: ({ alt }) => (
    <Box component="span" sx={{ fontStyle: "italic", color: textSecondary, fontSize: 13.5 }}>
      {alt ? `[image: ${alt}]` : "[image omitted]"}
    </Box>
  ),
});

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function BotInterface({ botType }) {
  const isPatient = botType === "patient";
  const botLabel  = isPatient ? "AIden" : "ClAIre";
  const botAccent = isPatient ? BRAND.gold : BRAND.navyLight;

  const [files, setFiles]                   = useState([]);
  const [previews, setPreviews]             = useState([]);
  const [analyzing, setAnalyzing]           = useState(false);
  const [uploadPhase, setUploadPhase]       = useState("");
  const [answer, setAnswer]                 = useState("");
  const [followUps, setFollowUps]           = useState([]);
  const [question, setQuestion]             = useState("");
  const [toasts, setToasts]                 = useState([]);
  const [history, setHistory]               = useState([]);
  const [isSpeaking, setIsSpeaking]         = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("english");
  // Part 6c — optional report-type selector. "" (default) = today's
  // existing, completely unchanged behavior; never auto-selected.
  const [reportType, setReportType] = useState("");
  // True only after a successful Proove-typed (_bot_typed) upload.
  // 2026-10-15 (user-directed correction) — SUPERSEDES the original note
  // below: the backend now writes typed results into session state
  // (main.py's analyze_*_bot_typed → _write_session_state(is_typed=True,
  // ...)), and a typed-aware follow-up endpoint exists
  // (/ask/{botType}_bot_typed — see askEndpoint/genericAskEndpoint above),
  // so this flag now ROUTES follow-up Q&A/Save-to-Memory/Clear-Memory to
  // the correct typed-aware paths instead of disabling them. Trends are
  // still not rendered for a typed result (no Proove-specific chart
  // mapper was built — _build_trends safely skips a Proove entry's dict-
  // shaped structured_results rather than charting it).
  // Original note, for history: "gates the follow-up/memory/trends UI
  // below, which all depend on server-side session state ... that the
  // typed endpoints never write to ... Disabling follow-up/memory for a
  // typed result avoids guessing at that behavior; a real fix (a typed-
  // aware follow-up endpoint) is backend work, out of scope here."
  const [isTypedResult, setIsTypedResult] = useState(false);
  // 2026-10-16 (downstream-mismatch-checker-flagged real gap) — a typed
  // analyze response can be a 200 whose own session-state write was
  // skipped (superseded by a newer upload on this account) — the analysis
  // shown is still correct, but follow-up/Save-to-Memory grounding was
  // lost server-side with no visible signal unless the frontend reads the
  // new `session_grounded` field and disables those two controls.
  const [sessionGrounded, setSessionGrounded] = useState(true);
  // 2026-10-16 (ui-ux-checker-flagged real bug) — a single shared
  // `analyzing` boolean used to gate follow-up/save/clear too, unmounting
  // the entire result view and swapping in the full-page upload loader for
  // a one-line Q&A or button click. Separate busy state for those three
  // actions so only a real document upload triggers that loader.
  const [busyAction, setBusyAction] = useState(null); // null | "asking" | "saving" | "clearing"
  // 2026-10-16 (verifier-flagged real gap) — the busyAction split above
  // only guarded each action against ITSELF, not against the others (and
  // never against `analyzing`, the upload flag) — so Upload-during-Ask,
  // Save-during-Ask, and Clear-during-Save were all still possible despite
  // looking mutually exclusive. One shared flag, checked by every button
  // below alongside its own specific condition.
  const anyBusy = analyzing || busyAction !== null;
  // 2026-10-16 (ui-ux-checker-flagged real bug) — the typed disclaimer used
  // to be concatenated onto `answer` and animated through the same
  // typewriter effect as the main analysis, so it could appear 30+ seconds
  // late with no warning styling. Kept separate, rendered immediately.
  const [typedDisclaimer, setTypedDisclaimer] = useState("");
  const [useAnimation, setUseAnimation]     = useState(true);
  const [useMemory, setUseMemory]           = useState(false);
  const [showHistory, setShowHistory]       = useState(false);
  const [dragOver, setDragOver]             = useState(false);
  // "My Results" ecosystem plan — dual input mode. "upload" (default,
  // unchanged flow) or "browse" (pick an already-analyzed stored report).
  const [inputMode, setInputMode]           = useState("upload");
  const [browseReports, setBrowseReports]   = useState([]);
  const [browseLoading, setBrowseLoading]   = useState(false);
  const [browseError, setBrowseError]       = useState("");
  // ui-ux-checker finding (2026-10-21) — "the feature isn't on yet"
  // (benign, expected) used to be silently folded into the same empty
  // `browseReports` state as "you genuinely have zero reports," so a
  // user with real reports waiting could be told they have none.
  const [browseFeatureDisabled, setBrowseFeatureDisabled] = useState(false);
  const [browseSelectedId, setBrowseSelectedId] = useState(null);
  const [reportMetadata, setReportMetadata] = useState([]);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [analysisTimestamp, setAnalysisTimestamp] = useState("");
  const [trendsData, setTrendsData]         = useState(null);
  const [trendsError, setTrendsError]       = useState(false); // 2026-10-15 fix — see fetchTrends' own comment
  const [hasSaved, setHasSaved]             = useState(false);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  // 2026-10-18 fix (real responsive gap, confirmed via a page survey) —
  // the left sidebar below hard-coded `width: 320` with no breakpoint
  // override at all. On an actual 320px-wide phone (iPhone SE and
  // similar), that fills the ENTIRE screen width (flexShrink: 0 in a row
  // flex container), clipping the chat pane — the single most-used
  // surface in this app — completely out of view. Hidden on phone
  // instead, same "secondary panel hidden on xs" convention LabReports.js
  // already uses for its own sidebar in this app.
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // 2026-10-18 fix (ui-ux-checker finding, real regression in the above
  // fix) — hiding the sidebar outright on mobile also hid the ONLY file
  // input, "Analyze Report" button, language selector, and memory
  // controls, since they live inside it — made the core upload flow
  // completely unusable on a phone, a worse bug than the clipping this
  // was meant to fix. Toggle between the controls and the results pane
  // instead of hiding one permanently; auto-switches to results once
  // there's something to see (effect below), and a header button (added
  // below) lets the user switch back to upload another file.
  const [mobileView, setMobileView] = useState("controls");
  useEffect(() => {
    if (isMobile && (analyzing || answer)) setMobileView("results");
  }, [isMobile, analyzing, answer]);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const printRef    = useRef(null);

  const { user } = useContext(UserContext);
  const authHeaders = useMemo(
    () => (user?.token ? { Authorization: `Bearer ${user.token}` } : {}),
    [user?.token]
  );

  const analyzeEndpoint = `${API_BASE}/analyze/${botType}_bot`;
  // Generic (non-typed) ask endpoint — ALWAYS used by handleUpload's
  // generic (non-Proove) branch below, regardless of isTypedResult. This
  // must NOT be the typed-aware `askEndpoint` further down: handleUpload
  // runs after `setIsTypedResult(false)`, but that state update isn't
  // visible until the next render, so a const computed from isTypedResult
  // at render time could still read a stale `true` from the PREVIOUS
  // (typed) result on the very next upload and wrongly post
  // "__AUTO_SUMMARY_VISION__" to the typed endpoint, which 409s because
  // analyze_generic's own reset just cleared is_typed. Using this
  // unconditional endpoint in handleUpload's generic branch avoids that
  // entirely.
  const genericAskEndpoint = useMemory
    ? `${API_BASE}/ask/memory_${botType}_bot`
    : `${API_BASE}/ask/${botType}_bot`;
  // 2026-10-15 (user-directed correction) — a Proove-typed result routes
  // follow-up Q&A to the new /ask/{bot}_bot_typed endpoint (session-state-
  // grounded, ephemeral — see main.py's ask_patient_bot_typed/
  // ask_clinician_bot_typed) instead of the generic endpoint above, which
  // would answer from the wrong (non-typed) prompt/dispatch path for this
  // result. Only read inside handleAskQuestion (a later render, after
  // isTypedResult has settled from the upload that just finished) — never
  // inside handleUpload itself; see genericAskEndpoint's comment above.
  const askEndpoint = isTypedResult
    ? `${API_BASE}/ask/${botType}_bot_typed`
    : genericAskEndpoint;

  const langConfig = LANGUAGE_OPTIONS.find((l) => l.value === selectedLanguage) || LANGUAGE_OPTIONS[0];
  const phase = PHASES.find((p) => p.key === uploadPhase) || { label: "Working…", pct: 50 };

  const bg            = isDark ? "#080d1a" : "#f4f7ff";
  const sidebar       = isDark ? "rgba(15,24,48,0.85)" : "rgba(255,255,255,0.88)";
  const border        = isDark ? "#1e2d50" : "#e2e8f0";
  const textPrimary   = isDark ? "#e8edf5" : BRAND.textDark;
  const textSecondary = isDark ? "#8899b8" : "#475569";

  // ── Toast helpers ─────────────────────────────────────────────────────────
  const addToast = useCallback((toast) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, ...toast }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // ── Speech synthesis ─────────────────────────────────────────────────────
  const handleToggleReadAloud = useCallback(async () => {
    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }
    if (!answer) return;
    const voices  = await loadVoices();
    const matched =
      voices.find((v) => langConfig.voiceHint && v.name.includes(langConfig.voiceHint)) ||
      voices.find((v) => v.lang.startsWith(langConfig.lang.slice(0, 2))) ||
      null;
    if (!matched && !voices.length) {
      addToast({ type: "error", message: "Read-aloud is not available in this browser." });
      return;
    }
    const readContent = followUps.length > 0 ? followUps[followUps.length - 1].content : answer;
    const plain  = readContent.replace(/[*#_`~]/g, "").replace(/\n+/g, " ");
    const chunks = chunkText(plain, 220);
    setIsSpeaking(true);
    speakChunks(chunks, matched, langConfig.lang, 1.0, () => setIsSpeaking(false));
  }, [answer, isSpeaking, langConfig, followUps, addToast]);

  // ── File handling ─────────────────────────────────────────────────────────
  const processFiles = (selected) => {
    const filtered = Array.from(selected).filter((f) =>
      ["application/pdf", "image/png", "image/jpeg", "image/jpg"].includes(f.type)
    );
    setFiles(filtered);
    setPreviews(
      filtered.map((f) => ({
        name: f.name,
        type: f.type,
        url: f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
      }))
    );
  };
  const handleFileChange = (e) => processFiles(e.target.files);
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    processFiles(e.dataTransfer.files);
  };

  // ── Fetch trends ──────────────────────────────────────────────────────────
  // 2026-10-15 fix (client-facing audit-log completeness sweep finding) —
  // a genuine fetch failure here used to be silently swallowed into the
  // exact same `trendsData === null` state as "this patient genuinely has
  // no trend charts yet" (e.g. first visit). trendsError lets the render
  // below tell those two cases apart.
  const fetchTrends = useCallback(async () => {
    setTrendsError(false);
    try {
      const res = await axios.get(`${API_BASE}/trends/${botType}_bot`, { headers: authHeaders });
      if (res.data?.charts?.length > 0) setTrendsData(res.data);
      else setTrendsData(null);
    } catch {
      setTrendsData(null);
      setTrendsError(true);
    }
  }, [botType, authHeaders]);

  // ── "Browse My Tests" — the second (dual) input mode ─────────────────────
  // Lists the user's OWN already-analyzed stored reports — patient via
  // GET /my-results (identity_id-scoped), clinician via GET /my-patients-
  // results (clinician_sub-scoped). Both routes already enforce "see only
  // your own" server-side; this list is never re-filtered client-side,
  // it's simply whatever those two already-reviewed routes return.
  const browseListEndpoint = isPatient ? `${API_BASE}/my-results` : `${API_BASE}/my-patients-results`;

  const handleBrowseFetch = useCallback(async () => {
    setBrowseLoading(true);
    setBrowseError("");
    setBrowseFeatureDisabled(false);
    try {
      const res = await axios.get(browseListEndpoint, { headers: authHeaders });
      if (res.data?.feature_disabled) {
        setBrowseReports([]);
        setBrowseFeatureDisabled(true);
      } else {
        setBrowseReports(res.data.results || []);
      }
    } catch (err) {
      setBrowseError(extractErrorDetail(err, "Couldn't load your stored reports right now."));
      setBrowseReports([]);
    } finally {
      setBrowseLoading(false);
    }
  }, [browseListEndpoint, authHeaders]);

  useEffect(() => {
    if (inputMode === "browse" && BROWSE_MY_TESTS_ENABLED) handleBrowseFetch();
  }, [inputMode, handleBrowseFetch]);

  // Mirrors handleUpload's own two branches (typed vs. generic) exactly —
  // same state-setting, same final shape — the only difference is where
  // the data comes from (POST /browse-my-tests/{botType}, which seeds the
  // SAME session state analyze_generic/analyze_*_bot_typed do, just from
  // an already-stored report instead of a freshly OCR'd upload — see that
  // endpoint's own module comment in main.py for why no vision call is
  // needed here at all).
  const handleBrowseSelect = async (reportId) => {
    setBrowseSelectedId(reportId);
    setAnalyzing(true);
    setAnswer("");
    setTypedDisclaimer("");
    setHistory([]);
    setReportMetadata([]);
    setTrendsData(null);
    setHasSaved(false);
    setIsTypedResult(false);
    setSessionGrounded(true);
    try {
      setUploadPhase("loadingStored");
      const res = await axios.post(
        `${API_BASE}/browse-my-tests/${botType}`,
        { report_id: reportId },
        { headers: authHeaders },
      );
      await new Promise((r) => setTimeout(r, 200));

      if (res.data.mode === "typed") {
        const summaryMd = typedHtmlToMarkdown(res.data.clinical_summary_html);
        const disclaimerMd = typedHtmlToMarkdown(res.data.disclaimer);
        setAnswer(summaryMd);
        setTypedDisclaimer(disclaimerMd);
        setFollowUps([]);
        setReportMetadata([]);
        setHistory([{ type: "Initial Analysis", content: summaryMd }]);
        setAnalysisTimestamp(formatEST(new Date().toISOString()));
        setIsTypedResult(true);
        // 2026-10-21 fix (verifier finding) — handleUpload's own typed
        // branch warns the user when grounding was lost to a race (see
        // its own comment above); this used to omit that warning, which
        // was harmless only because the backend used to always return
        // true here regardless — now that it genuinely can return
        // false (a second concurrent request on this account winning
        // the same race), this needs the same toast.
        const grounded = res.data.session_grounded !== false;
        setSessionGrounded(grounded);
        if (!grounded) {
          addToast({
            type: "warning",
            message: "A newer report was loaded on this account while this one was loading. "
              + "This result is still accurate, but follow-up questions and Save to Memory may not work for it — please select it again if you need those.",
            duration: 9000,
          });
        }
      } else {
        // generic mode — `answer` is the stored report's own ai_summary,
        // which is HTML (same field MyResults.js renders via
        // dangerouslySetInnerHTML) — converted to Markdown here, same as
        // the typed branch's clinical_summary_html/disclaimer above,
        // since this component renders `answer` through ReactMarkdown,
        // not raw HTML, everywhere else.
        const summaryMd = typedHtmlToMarkdown(res.data.answer);
        setAnswer(summaryMd);
        setFollowUps([]);
        setReportMetadata(res.data.metadata || []);
        setHistory([{ type: "Initial Analysis", content: summaryMd }]);
        setAnalysisTimestamp(formatEST(new Date().toISOString()));
        if (useMemory) fetchTrends();
      }
    } catch (err) {
      addToast({ type: "error", message: extractErrorDetail(err, err.message || "Couldn't load this report.") });
    } finally {
      setAnalyzing(false);
      setUploadPhase("");
    }
  };

  // ── Analyze ───────────────────────────────────────────────────────────────
  const handleUpload = async () => {
    if (!files.length) return;
    setAnalyzing(true);
    setAnswer("");
    setTypedDisclaimer("");
    setHistory([]);
    setReportMetadata([]);
    setTrendsData(null);
    setHasSaved(false);
    setIsTypedResult(false);
    setSessionGrounded(true);

    try {
      setUploadPhase("uploading");

      if (PROOVE_REPORT_TYPE_IDS.has(reportType)) {
        // Part 6c — Proove-family selection: route to Part 6a's typed
        // endpoint instead of the existing analyzeEndpoint/askEndpoint pair.
        // The typed endpoint returns the full answer in ONE call (no
        // separate "__AUTO_SUMMARY_VISION__" askEndpoint round-trip — that
        // endpoint only knows the generic, non-typed summary path).
        const typedFormData = new FormData();
        files.forEach((f) => typedFormData.append("files", f));
        typedFormData.append("report_type", reportType);
        // `language` here is Part 6a's own form field ("Respond in
        // {language}", capped 40 chars server-side) — a distinct contract
        // from askEndpoint's `target_language` slug used below, so the
        // human-readable English language name is sent, not the raw
        // lowercase value.
        typedFormData.append(
          "language",
          selectedLanguage ? selectedLanguage[0].toUpperCase() + selectedLanguage.slice(1) : "English"
        );

        setUploadPhase("analyzing");
        const typedRes = await axios.post(
          `${API_BASE}/analyze/${botType}_bot_typed`,
          typedFormData,
          { headers: authHeaders }
        );

        setUploadPhase("summarizing");
        await new Promise((r) => setTimeout(r, 300));

        const summaryMd = typedHtmlToMarkdown(typedRes.data.clinical_summary_html);
        const disclaimerMd = typedHtmlToMarkdown(typedRes.data.disclaimer);

        setAnswer(summaryMd);
        setTypedDisclaimer(disclaimerMd);
        setFollowUps([]);
        // `extracted` (typedRes.data.extracted) is a flat clinical-fields
        // dict, not the {field,value}[] shape this metadata card expects —
        // deliberately left unmapped rather than guessed at, to avoid
        // mislabeling real clinical values in the UI.
        setReportMetadata([]);
        setHistory([{ type: "Initial Analysis", content: summaryMd }]);
        setAnalysisTimestamp(formatEST(new Date().toISOString()));
        setIsTypedResult(true);
        // 2026-10-16 (downstream-mismatch-checker-flagged real gap) — see
        // sessionGrounded's own comment above.
        const grounded = typedRes.data.session_grounded !== false;
        setSessionGrounded(grounded);
        if (!grounded) {
          addToast({
            type: "warning",
            message: "A newer report was uploaded on this account while this one was being analyzed. "
              + "This result is still accurate, but follow-up questions and Save to Memory may not work for it — please re-upload if you need those.",
            duration: 9000,
          });
        }
        // No fetchTrends() call here on upload itself — a typed result has
        // no chart to show yet (nothing's been saved). handleSaveToMemory
        // DOES call fetchTrends() after a successful typed save, same as
        // the generic path, now that /add_to_memory/{bot}_bot is typed-
        // aware server-side; it will just come back empty today (no
        // Proove-specific chart mapper exists yet — see isTypedResult's
        // own comment, above).
      } else {
        const formData = new FormData();
        files.forEach((f) => formData.append("files", f));
        // Part 6b — optional report_family hint, only ever sent for a
        // recognized Labarium (non-Proove) selection. Omitted entirely for
        // the default "Not sure / other" option (reportType === ""), so
        // that path's wire request is byte-identical to before this change.
        const reportFamily = LABARIUM_REPORT_TYPE_TO_FAMILY[reportType];
        if (reportFamily) formData.append("report_family", reportFamily);
        await axios.post(analyzeEndpoint, formData, { headers: authHeaders });

        setUploadPhase("processing");
        await new Promise((r) => setTimeout(r, 400));

        setUploadPhase("analyzing");
        const res = await axios.post(
          genericAskEndpoint,
          { question: "__AUTO_SUMMARY_VISION__", target_language: selectedLanguage },
          { headers: authHeaders }
        );

        setUploadPhase("summarizing");
        await new Promise((r) => setTimeout(r, 300));

        const summary = res.data.answer;
        setAnswer(summary);
        setFollowUps([]);
        setReportMetadata(res.data.metadata || []);
        setHistory([{ type: "Initial Analysis", content: summary }]);
        setAnalysisTimestamp(formatEST(new Date().toISOString())); // 2026-10-04 — explicit instruction: every timestamp shows in EST, labeled
        if (useMemory) fetchTrends();
      }
    } catch (err) {
      addToast({ type: "error", message: extractErrorDetail(err, err.message || "Analysis failed.") });
    } finally {
      setAnalyzing(false);
      setUploadPhase("");
    }
  };

  // ── Follow-up ─────────────────────────────────────────────────────────────
  const handleAskQuestion = async () => {
    if (!question.trim()) return;
    // 2026-10-15 (user-directed correction) — follow-up Q&A is now
    // supported for a Proove-typed result too (routed to the typed
    // askEndpoint above); the old unconditional isTypedResult guard here
    // is gone.
    if (question.trim().length > 2000) {
      addToast({ type: "error", message: "Question is too long (max 2000 characters)." });
      return;
    }
    setBusyAction("asking");
    const q = question.trim();
    try {
      const res = await axios.post(
        askEndpoint,
        { question: q, target_language: selectedLanguage },
        { headers: authHeaders }
      );
      // 2026-10-16 (ui-ux-checker-flagged real bug) — moved to the success
      // path: this used to clear the question BEFORE sending, so a failed
      // (409/500) request lost the user's typed question with no way to
      // recover it except retyping from memory.
      setQuestion("");
      const response = res.data.answer;
      setFollowUps((prev) => [...prev, { question: q, content: response }]);
      setHistory((prev) => [...prev, { type: q, content: response }]);
    } catch (err) {
      addToast({ type: "error", message: extractErrorDetail(err, err.message || "Failed to get answer.") });
    } finally {
      setBusyAction(null);
    }
  };

  // ── Memory ────────────────────────────────────────────────────────────────
  const handleSaveToMemory = async () => {
    if (hasSaved) return;
    setBusyAction("saving");
    try {
      await axios.post(`${API_BASE}/add_to_memory/${botType}_bot`, {}, { headers: authHeaders });
      setHasSaved(true);
      addToast({ type: "success", message: "Analysis saved to long-term memory." });
      fetchTrends();
    } catch (err) {
      const status = err.response?.status;
      if (status === 409) {
        setHasSaved(true);
        addToast({ type: "warning", message: "Already in memory — this report was saved previously." });
      } else {
        addToast({ type: "error", message: extractErrorDetail(err, "Failed to save to memory.") });
      }
    } finally {
      setBusyAction(null);
    }
  };

  const handleClearMemory = () => setConfirmClearOpen(true);

  const handleConfirmClear = async () => {
    setConfirmClearOpen(false);
    setBusyAction("clearing");
    try {
      await axios.post(`${API_BASE}/clear_${botType}_bot_memories`, {}, { headers: authHeaders });
      addToast({ type: "success", message: "Memory cleared successfully." });
      setHistory([]);
      setHasSaved(false);
      setTrendsData(null);
      // 2026-10-16 (downstream-mismatch-checker-flagged real bug) — this
      // endpoint deletes the live typed session state (is_typed-scoped),
      // so a typed result left on screen afterward is already stale: the
      // next follow-up would 409 while the "ANALYSIS COMPLETE" header and
      // an enabled follow-up box kept implying otherwise. A generic
      // result's own session is never touched by this endpoint, so only
      // the typed case needs clearing here.
      if (isTypedResult) {
        setAnswer("");
        setTypedDisclaimer("");
        setFollowUps([]);
        setReportMetadata([]);
        setIsTypedResult(false);
        setSessionGrounded(true);
      }
    } catch (err) {
      addToast({ type: "error", message: extractErrorDetail(err, "Failed to clear memory.") });
    } finally {
      setBusyAction(null);
    }
  };

  // ── PDF ───────────────────────────────────────────────────────────────────
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Labarium ${botLabel} Report - ${new Date().toLocaleDateString()}`,
    onBeforePrint: async () => setDownloadingPdf(true),
    onAfterPrint:  () => setDownloadingPdf(false),
  });
  const handleDownloadPDF = () => { if (answer) handlePrint(); };

  // ── Cleanup ───────────────────────────────────────────────────────────────
  useEffect(
    () => () => previews.forEach((p) => p.url && URL.revokeObjectURL(p.url)),
    [previews]
  );
  useEffect(() => {
    const stop = () => { if (isSpeaking) { window.speechSynthesis.cancel(); setIsSpeaking(false); } };
    window.addEventListener("beforeunload", stop);
    return () => window.removeEventListener("beforeunload", stop);
  }, [isSpeaking]);

  const typedAnswer = useTypewriter(answer, 12);

  // ── Gradient border helper ────────────────────────────────────────────────
  const zoneBg = isDark ? "#0a1020" : "#f5f8ff";
  const zoneBorderGradient = dragOver
    ? `linear-gradient(${zoneBg}, ${zoneBg}) padding-box, linear-gradient(135deg, ${BRAND.navy}, ${BRAND.gold}, ${BRAND.navyLight}) border-box`
    : files.length
      ? `linear-gradient(${zoneBg}, ${zoneBg}) padding-box, linear-gradient(135deg, ${BRAND.gold}77, ${BRAND.navy}77) border-box`
      : `linear-gradient(${zoneBg}, ${zoneBg}) padding-box, linear-gradient(135deg, ${border}, ${border}) border-box`;

  // ==========================================================================
  //  RENDER
  // ==========================================================================
  // 2026-10-16 (ui-ux-checker-flagged real gap) — none of this file's
  // framer-motion usage (toasts, modal, progress bars, drag-zone, memory
  // panel) respected prefers-reduced-motion, despite toasts firing on
  // every save/clear/error — a frequently-seen surface. MotionConfig
  // wraps the whole tree once here rather than touching every motion.div
  // individually; reducedMotion="user" defers to the OS-level setting.
  return (
    <MotionConfig reducedMotion="user">
    <Box sx={{
      height: "100vh", display: "flex", flexDirection: "column",
      overflow: "hidden", bgcolor: bg,
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>

      {/* ── Header ── */}
      <Box component="header" sx={{
        background: `linear-gradient(to right, ${BRAND.navyDark}, ${BRAND.navy})`,
        borderBottom: `2px solid ${BRAND.gold}44`,
        flexShrink: 0,
        boxShadow: "0 4px 24px rgba(0,0,0,0.2)",
      }}>
        <Box sx={{ px: 3, py: 1, minHeight: 64, display: "flex", alignItems: "center", flexWrap: "wrap", rowGap: 1,
          justifyContent: "space-between", maxWidth: 1440, mx: "auto", width: "100%" }}>
          <Box component="a" href="https://labarium.com/" target="_blank" rel="noopener noreferrer"
            sx={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
            <img src="/logo-labarium-dark.png" alt="Labarium" style={{ height: 46, width: "auto" }} />
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {/* Live badge */}
            <Box sx={{
              display: { xs: "none", sm: "flex" }, alignItems: "center", gap: 1,
              bgcolor: `${botAccent}18`,
              border: `1px solid ${botAccent}44`,
              borderRadius: "10px", px: 1.25, py: 0.4,
              backdropFilter: "blur(8px)",
            }}>
              <Box sx={{
                width: 40, height: 40, borderRadius: "50%", overflow: "hidden", flexShrink: 0,
                border: `2px solid rgba(192,168,140,0.55)`,
                boxShadow: "0 0 10px rgba(0,0,0,0.35)",
              }}>
                <img
                  src={isPatient ? "/aiden-avatar.png" : "/claire-avatar.png"}
                  alt={botLabel}
                  style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "50% 12%" }}
                />
              </Box>
              <Box sx={{
                width: 7, height: 7, borderRadius: "50%", bgcolor: botAccent,
                "@keyframes pulse": { "0%,100%": { opacity: 1, transform: "scale(1)" }, "50%": { opacity: 0.4, transform: "scale(0.8)" } },
                animation: "pulse 2s infinite",
              }} />
              <Typography sx={{ fontSize: 15, fontWeight: 700, letterSpacing: "0.08em", color: "rgba(255,255,255,0.92)" }}>
                {isPatient ? (
                  <><Box component="span" sx={{ color: BRAND.gold, fontWeight: 900 }}>AI</Box>den</>
                ) : (
                  <>Cl<Box component="span" sx={{ color: BRAND.gold, fontWeight: 900 }}>AI</Box>re</>
                )}
              </Typography>
            </Box>
            <ThemeToggle />
            {/* 2026-10-18 fix — mobile-only toggle between the upload
                controls and the results pane (see mobileView state above).
                Only shown once there's a results pane worth switching to. */}
            {isMobile && (answer || analyzing) && (
              <Button
                onClick={() => setMobileView(mobileView === "controls" ? "results" : "controls")}
                size="small"
                sx={{
                  color: "rgba(255,255,255,0.65)",
                  fontSize: 14.5, fontWeight: 600, px: 1.5, py: 0.6,
                  borderRadius: "8px", textTransform: "none", minWidth: 0,
                  border: `1px solid ${BRAND.gold}66`,
                }}
              >
                {mobileView === "controls" ? "View Results" : (inputMode === "browse" ? "Choose Another" : "Upload Another")}
              </Button>
            )}
            {/* Home button */}
            <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}>
              <Button
                onClick={() => navigate("/home")}
                size="small"
                startIcon={<HomeIcon sx={{ fontSize: "15px !important" }} />}
                sx={{
                  color: "rgba(255,255,255,0.65)",
                  fontSize: 14.5, fontWeight: 600, px: 1.5, py: 0.6,
                  borderRadius: "8px", textTransform: "none", minWidth: 0,
                  border: "1px solid transparent",
                  transition: "all 0.2s",
                  "&:hover": {
                    color: BRAND.gold,
                    bgcolor: "rgba(192,168,140,0.1)",
                    border: `1px solid ${BRAND.gold}33`,
                  },
                }}
              >
                <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>Home</Box>
              </Button>
            </motion.div>
            <NavUserMenu />
          </Stack>
        </Box>
      </Box>

      {/* ── Body ── */}
      <Box sx={{ flex: 1, display: "flex", overflow: "hidden", maxWidth: 1440, mx: "auto", width: "100%" }}>

        {/* ─── LEFT SIDEBAR ─── */}
        {(!isMobile || mobileView === "controls") && (
        <motion.div
          initial={{ x: -30, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
          style={{ width: isMobile ? "100%" : 320, flexShrink: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}
        >
          <Box sx={{
            width: "100%", flexShrink: 0,
            bgcolor: sidebar,
            backdropFilter: "blur(24px) saturate(1.4)",
            borderRight: { md: `1px solid ${border}` },
            display: "flex", flexDirection: "column", overflowY: "auto",
            height: "100%",
          }}>
            {/* Sidebar header */}
            <Box sx={{ px: 3, py: 2.5, borderBottom: `1px solid ${border}`, flexShrink: 0 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 800, letterSpacing: "0.12em", color: isDark ? "rgba(255,255,255,0.92)" : BRAND.navy, mb: 0.4 }}>
                {isPatient ? (
                  <><Box component="span" sx={{ color: BRAND.gold }}>AI</Box>den — Patient Assistant</>
                ) : (
                  <>Cl<Box component="span" sx={{ color: BRAND.gold }}>AI</Box>re — Decision Support</>
                )}
              </Typography>
              <Typography sx={{ fontSize: 15, color: textSecondary, lineHeight: 1.6 }}>
                {isPatient
                  ? "Upload a lab report for a plain-language interpretation of every result."
                  : "Upload a patient report for evidence-based clinical analysis with citations."}
              </Typography>
            </Box>

            {/* "My Results" ecosystem plan — dual input mode toggle.
                Only rendered when BROWSE_MY_TESTS_ENABLED; when disabled,
                this entire Box renders nothing and the page is byte-for-
                byte the existing upload-only flow. */}
            {BROWSE_MY_TESTS_ENABLED && (
              <Box sx={{ px: 3, pt: 2.5, display: "flex", gap: 1 }}>
                {[
                  { key: "upload", label: "Upload PDF" },
                  { key: "browse", label: "Browse My Tests" },
                ].map((opt) => (
                  <Box key={opt.key} onClick={() => !anyBusy && setInputMode(opt.key)}
                    role="button" tabIndex={0} aria-pressed={inputMode === opt.key} aria-disabled={anyBusy}
                    onKeyDown={(e) => { if (!anyBusy && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setInputMode(opt.key); } }}
                    sx={{
                      flex: 1, textAlign: "center", py: 1, borderRadius: "10px",
                      cursor: anyBusy ? "default" : "pointer", opacity: anyBusy ? 0.6 : 1,
                      fontSize: 13, fontWeight: 700, transition: "all 0.15s",
                      bgcolor: inputMode === opt.key ? BRAND.navy : "transparent",
                      color: inputMode === opt.key ? "#fff" : textSecondary,
                      border: `1px solid ${inputMode === opt.key ? BRAND.navy : border}`,
                      "&:focus-visible": { outline: `2px solid ${botAccent}`, outlineOffset: 2 },
                    }}>
                    {opt.label}
                  </Box>
                ))}
              </Box>
            )}

            {/* Browse My Tests — the second input mode. Lives alongside
                the upload dropzone below (display:none/block, never
                JSX-conditionally unmounted) so no JSX-boundary risk is
                introduced into the existing, live upload block right
                below this one. */}
            {BROWSE_MY_TESTS_ENABLED && (
              <Box sx={{ px: 3, pt: 2.5, display: inputMode === "browse" ? "block" : "none" }}>
                {browseLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={28} sx={{ color: BRAND.navy }} />
                  </Box>
                ) : browseError ? (
                  <Alert severity="error" sx={{ borderRadius: 2, fontSize: 13 }}
                    action={
                      <Box onClick={handleBrowseFetch} role="button" tabIndex={0}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleBrowseFetch(); } }}
                        sx={{ cursor: "pointer", fontWeight: 700, fontSize: 13, px: 1, "&:focus-visible": { outline: `2px solid ${botAccent}`, outlineOffset: 2 } }}>
                        Retry
                      </Box>
                    }>
                    {browseError}
                  </Alert>
                ) : browseFeatureDisabled ? (
                  <Typography sx={{ fontSize: 13.5, color: textSecondary, textAlign: "center", py: 2 }}>
                    This feature isn't available for your account yet.
                  </Typography>
                ) : browseReports.length === 0 ? (
                  <Typography sx={{ fontSize: 13.5, color: textSecondary, textAlign: "center", py: 2 }}>
                    {isPatient ? "No completed results yet." : "No completed results yet for your ordered tests."}
                  </Typography>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, maxHeight: 320, overflowY: "auto" }}>
                    {browseReports.map((r) => (
                      <Box key={r._id} onClick={() => !anyBusy && handleBrowseSelect(r._id)}
                        role="button" tabIndex={0} aria-disabled={anyBusy}
                        onKeyDown={(e) => { if (!anyBusy && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); handleBrowseSelect(r._id); } }}
                        sx={{
                          display: "flex", alignItems: "center", gap: 1, p: 1.25, borderRadius: "10px",
                          cursor: anyBusy ? "default" : "pointer", opacity: anyBusy ? 0.6 : 1,
                          border: `1px solid ${browseSelectedId === r._id ? BRAND.gold : border}`,
                          bgcolor: browseSelectedId === r._id ? "rgba(192,168,140,0.1)" : "transparent",
                          transition: "all 0.15s",
                          "&:hover": anyBusy ? {} : { borderColor: BRAND.gold },
                          "&:focus-visible": { outline: `2px solid ${botAccent}`, outlineOffset: 2 },
                        }}>
                        <FolderOpenIcon sx={{ fontSize: 18, color: BRAND.gold, flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: textPrimary }}>
                            {browseReportTypeLabel(r.report_type_id)}
                          </Typography>
                          {r.generated_at && (
                            <Typography sx={{ fontSize: 11.5, color: textSecondary }}>
                              {formatEST(r.generated_at, { dateOnly: true })}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {/* Upload drop zone — unchanged existing flow. Hidden (not
                unmounted) via display:none when Browse My Tests is the
                active mode, so this block's own internals never need to
                change for this feature. */}
            <Box sx={{ px: 3, pt: 3, display: BROWSE_MY_TESTS_ENABLED && inputMode === "browse" ? "none" : "block" }}>
              <motion.div
                animate={{ scale: dragOver ? 1.015 : 1 }}
                transition={{ type: "spring", stiffness: 400, damping: 28 }}
              >
                <Box
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDragEnd={() => setDragOver(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    background: zoneBorderGradient,
                    border: "2px solid transparent",
                    borderRadius: "16px", p: 3, textAlign: "center", cursor: "pointer",
                    bgcolor: zoneBg,
                    boxShadow: dragOver
                      ? `0 0 0 6px rgba(0,53,122,0.1), 0 8px 32px rgba(0,53,122,0.15)`
                      : files.length
                        ? `0 0 0 3px rgba(192,168,140,0.12)`
                        : "none",
                    transition: "all 0.25s ease",
                    "&:hover": {
                      boxShadow: `0 0 0 4px rgba(192,168,140,0.14), 0 4px 20px rgba(0,53,122,0.1)`,
                    },
                  }}
                >
                  <input ref={fileInputRef} type="file" hidden multiple accept=".pdf,image/*" onChange={handleFileChange} />
                  <motion.div
                    animate={{ scale: dragOver ? 1.22 : 1, rotate: dragOver ? 5 : 0 }}
                    transition={{ type: "spring", stiffness: 400, damping: 22 }}
                  >
                    <UploadFileIcon sx={{
                      fontSize: 38, mb: 1,
                      color: dragOver ? BRAND.navy : files.length ? BRAND.navy : BRAND.gold,
                      filter: dragOver ? `drop-shadow(0 0 10px ${BRAND.navy}88)` : "none",
                      transition: "color 0.2s",
                    }} />
                  </motion.div>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: textPrimary, mb: 0.5 }}>
                    {dragOver
                      ? "Release to upload"
                      : files.length > 0
                        ? `${files.length} file${files.length > 1 ? "s" : ""} selected`
                        : "Drop files or click to upload"}
                  </Typography>
                  <Typography sx={{ fontSize: 14.5, color: textSecondary }}>PDF · PNG · JPG</Typography>
                </Box>
              </motion.div>

              {/* Labarium-only disclaimer */}
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.4 }}
              >
                <Box sx={{
                  mt: 1.75, px: 1.5, py: 1.1,
                  bgcolor: isDark ? "rgba(192,168,140,0.07)" : "rgba(192,168,140,0.1)",
                  border: `1px solid ${isDark ? "rgba(192,168,140,0.18)" : "rgba(192,168,140,0.32)"}`,
                  borderRadius: "10px",
                  display: "flex", alignItems: "flex-start", gap: 1,
                }}>
                  <Typography sx={{ color: BRAND.gold, fontSize: 13.5, flexShrink: 0, mt: "1px", lineHeight: 1 }}>⚠</Typography>
                  <Typography sx={{ fontSize: 14.5, color: textSecondary, lineHeight: 1.55 }}>
                    <Box component="span" sx={{ fontWeight: 700, color: isDark ? BRAND.gold : BRAND.navy }}>
                      Labarium- and Proove-generated reports only.
                    </Box>{" "}
                    This tool is intended for lab reports generated by Labarium or Proove. Uploading any other
                    (third-party) report is at your own risk — Labarium is not responsible for the output produced
                    on an unsupported document type. Like all AI, outputs may occasionally contain errors — always
                    verify values with your healthcare provider.
                  </Typography>
                </Box>
              </motion.div>

              {/* File chips */}
              <AnimatePresence>
                {previews.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                      {previews.map((file, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.8 }}
                          transition={{ type: "spring", stiffness: 400, damping: 24 }}
                        >
                          <Tooltip title={file.name} placement="top" arrow>
                            <Box onClick={(e) => e.stopPropagation()} sx={{
                              display: "flex", alignItems: "center", gap: 0.75,
                              bgcolor: isDark ? "rgba(15,24,48,0.8)" : BRAND.offWhite,
                              border: `1px solid ${border}`,
                              boxShadow: `0 2px 8px rgba(0,0,0,0.06)`,
                              borderRadius: "10px", px: 1.25, py: 0.5,
                              transition: "box-shadow 0.15s",
                              "&:hover": { boxShadow: `0 4px 16px rgba(0,53,122,0.12)` },
                            }}>
                              {file.url
                                ? <img src={file.url} alt="" style={{ width: 22, height: 22, borderRadius: 4, objectFit: "cover" }} />
                                : <Box sx={{ width: 22, height: 22, background: `linear-gradient(135deg, ${BRAND.navy}, #1a4b9e)`,
                                    borderRadius: "4px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <Typography sx={{ color: "#fff", fontSize: 7, fontWeight: 800 }}>PDF</Typography>
                                  </Box>
                              }
                              <Typography sx={{ fontSize: 12.5, color: textPrimary, maxWidth: 110,
                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {file.name}
                              </Typography>
                              <Box
                                component="span"
                                sx={{
                                  ml: 0.25, cursor: "pointer", color: textSecondary, fontSize: 16, lineHeight: 1,
                                  width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center",
                                  borderRadius: "50%", transition: "all 0.15s",
                                  "&:hover": { color: "#ef4444", bgcolor: "rgba(239,68,68,0.12)" },
                                }}
                                onClick={() => {
                                  setFiles(files.filter((_, i) => i !== idx));
                                  setPreviews(previews.filter((_, i) => i !== idx));
                                }}>×</Box>
                            </Box>
                          </Tooltip>
                        </motion.div>
                      ))}
                    </Box>
                  </motion.div>
                )}
              </AnimatePresence>
            </Box>

            {/* Settings */}
            <Box sx={{ px: 3, pt: 3, pb: 2 }}>
              {/* Section divider */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
                <Box sx={{ flex: 1, height: "1px", bgcolor: border }} />
                <Typography sx={{
                  fontSize: 12.5, fontWeight: 800, letterSpacing: "0.18em",
                  color: textSecondary, textTransform: "uppercase", flexShrink: 0,
                }}>Configuration</Typography>
                <Box sx={{ flex: 1, height: "1px", bgcolor: border }} />
              </Box>

              {/* Language select */}
              <Box sx={{ mb: 2.5 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, letterSpacing: "0.1em",
                  color: textSecondary, textTransform: "uppercase", mb: 0.75 }}>
                  Response language
                </Typography>
                <Select
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  size="small" fullWidth
                  MenuProps={{ slotProps: { paper: { sx: {
                    borderRadius: "14px", mt: 0.75,
                    boxShadow: isDark
                      ? "0 16px 48px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.06)"
                      : "0 16px 48px rgba(0,53,122,0.16)",
                    bgcolor: isDark ? "#0f1830" : "#fff",
                    border: `1px solid ${border}`,
                  }}}}}
                  sx={{
                    fontSize: 15, fontWeight: 600, borderRadius: "10px",
                    bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,53,122,0.03)",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: border, borderWidth: "1.5px",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: `${botAccent}88` },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: botAccent, borderWidth: "2px",
                      boxShadow: `0 0 0 3px ${botAccent}20`,
                    },
                    "& .MuiSelect-select": { py: 1, px: 1.5 },
                    color: textPrimary,
                    transition: "all 0.2s",
                  }}
                >
                  {LANGUAGE_OPTIONS.map((l) => (
                    <MenuItem key={l.value} value={l.value} sx={{ fontSize: 15, fontWeight: 600, borderRadius: "8px", mx: 0.5 }}>{l.label}</MenuItem>
                  ))}
                </Select>
              </Box>

              {/* Part 6c — Report type (optional). Leaving this on "Not
                  sure / other" (value "") keeps today's existing upload
                  behavior completely unchanged — no report_type/report_family
                  field is ever sent for that option. Styling copied exactly
                  from the Response language Select immediately above.
                  ui-ux-checker finding (2026-10-21) — hidden in Browse My
                  Tests mode: this selection only ever affects the upload
                  path (which typed/generic branch handleUpload takes, and
                  the Form fields it sends) — handleBrowseSelect sends only
                  {report_id}, so it has no effect while visibly
                  interactive. The Response language select above this
                  stays visible either way — it's also read by
                  handleAskQuestion's follow-up calls, which work in
                  Browse mode too. */}
              <Box sx={{ mb: 2.5, display: BROWSE_MY_TESTS_ENABLED && inputMode === "browse" ? "none" : "block" }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, letterSpacing: "0.1em",
                  color: textSecondary, textTransform: "uppercase", mb: 0.75 }}>
                  Report type (optional)
                </Typography>
                <Select
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value)}
                  displayEmpty
                  size="small" fullWidth
                  MenuProps={{ slotProps: { paper: { sx: {
                    borderRadius: "14px", mt: 0.75,
                    boxShadow: isDark
                      ? "0 16px 48px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.06)"
                      : "0 16px 48px rgba(0,53,122,0.16)",
                    bgcolor: isDark ? "#0f1830" : "#fff",
                    border: `1px solid ${border}`,
                  }}}}}
                  sx={{
                    fontSize: 15, fontWeight: 600, borderRadius: "10px",
                    bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,53,122,0.03)",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: border, borderWidth: "1.5px",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: `${botAccent}88` },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: botAccent, borderWidth: "2px",
                      boxShadow: `0 0 0 3px ${botAccent}20`,
                    },
                    "& .MuiSelect-select": { py: 1, px: 1.5 },
                    color: textPrimary,
                    transition: "all 0.2s",
                  }}
                >
                  <MenuItem value="" sx={{ fontSize: 15, fontWeight: 600, borderRadius: "8px", mx: 0.5 }}>
                    Not sure / other (default)
                  </MenuItem>
                  {/* 2026-10-16 (ui-ux-checker-flagged real bug) — MUI's
                      sticky ListSubheader paints its own background from
                      theme.palette.background.paper (dark-mode default
                      #121212) when not told otherwise; this menu's Paper is
                      hardcoded #0f1830 in dark mode, so without an explicit
                      bgcolor here the two group headers show a visibly
                      mismatched band behind them. */}
                  <ListSubheader sx={{ fontSize: 12.5, fontWeight: 800, letterSpacing: "0.08em", color: textSecondary, lineHeight: "32px", bgcolor: isDark ? "#0f1830" : "#fff" }}>
                    Proove Reports
                  </ListSubheader>
                  {PROOVE_REPORT_TYPE_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value} sx={{ fontSize: 15, fontWeight: 600, borderRadius: "8px", mx: 0.5 }}>{o.label}</MenuItem>
                  ))}
                  <ListSubheader sx={{ fontSize: 12.5, fontWeight: 800, letterSpacing: "0.08em", color: textSecondary, lineHeight: "32px", bgcolor: isDark ? "#0f1830" : "#fff" }}>
                    Labarium Reports
                  </ListSubheader>
                  {LABARIUM_REPORT_TYPE_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value} sx={{ fontSize: 15, fontWeight: 600, borderRadius: "8px", mx: 0.5 }}>{o.label}</MenuItem>
                  ))}
                </Select>
              </Box>

              {/* Toggles */}
              <Stack spacing={1}>
                {[
                  {
                    checked: useAnimation,
                    onChange: setUseAnimation,
                    label: "Typing animation",
                    sub: "Stream response character by character",
                  },
                  {
                    checked: useMemory,
                    onChange: setUseMemory,
                    label: "Long-term memory",
                    sub: "Trend detection across visits",
                    badge: useMemory ? "ON" : null,
                    tooltip: "Prior saved analyses are used as context for trend detection",
                  },
                ].map(({ checked, onChange, label, sub, badge, tooltip }) => (
                  <Tooltip key={label} title={tooltip || ""} placement="right" disableHoverListener={!tooltip}>
                    <motion.div whileHover={{ scale: 1.01 }} transition={{ type: "spring", stiffness: 400, damping: 28 }}>
                      <Box
                        onClick={() => onChange(!checked)}
                        sx={{
                          display: "flex", alignItems: "center", justifyContent: "space-between",
                          bgcolor: checked
                            ? (isDark ? `${botAccent}14` : `${botAccent}0e`)
                            : (isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)"),
                          border: `1px solid ${checked ? `${botAccent}44` : border}`,
                          borderRadius: "12px", px: 1.75, py: 1,
                          cursor: "pointer",
                          transition: "all 0.22s ease",
                          "&:hover": {
                            bgcolor: checked
                              ? (isDark ? `${botAccent}1e` : `${botAccent}16`)
                              : (isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)"),
                          },
                        }}
                      >
                        <Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 600, color: textPrimary, lineHeight: 1.2 }}>
                              {label}
                            </Typography>
                            <AnimatePresence>
                              {badge && (
                                <motion.div
                                  initial={{ scale: 0, opacity: 0 }}
                                  animate={{ scale: 1, opacity: 1 }}
                                  exit={{ scale: 0, opacity: 0 }}
                                  transition={{ type: "spring", stiffness: 500, damping: 24 }}
                                >
                                  <Box sx={{
                                    fontSize: 8.5, fontWeight: 800, letterSpacing: "0.08em",
                                    color: "#fff", bgcolor: botAccent, borderRadius: "5px",
                                    px: 0.7, py: 0.15, lineHeight: 1.5,
                                  }}>{badge}</Box>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </Box>
                          <Typography sx={{ fontSize: 13.5, color: textSecondary, mt: 0.2 }}>{sub}</Typography>
                        </Box>
                        <Switch
                          checked={checked}
                          size="small"
                          onChange={(e) => { e.stopPropagation(); onChange(e.target.checked); }}
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            "& .MuiSwitch-track": {
                              bgcolor: checked ? `${botAccent} !important` : undefined,
                              opacity: checked ? "0.9 !important" : undefined,
                              transition: "background-color 0.3s ease",
                            },
                            "& .MuiSwitch-thumb": {
                              bgcolor: "#fff",
                              boxShadow: checked ? `0 0 6px ${botAccent}88` : "0 1px 3px rgba(0,0,0,0.2)",
                              transition: "box-shadow 0.3s ease",
                            },
                          }}
                        />
                      </Box>
                    </motion.div>
                  </Tooltip>
                ))}
              </Stack>
            </Box>

            {/* Primary CTA — hidden in Browse My Tests mode: selecting a
                row there loads it immediately (handleBrowseSelect), no
                separate "Analyze" click needed. */}
            <Box sx={{ px: 3, pb: 3, mt: "auto", display: BROWSE_MY_TESTS_ENABLED && inputMode === "browse" ? "none" : "block" }}>
              <motion.div whileTap={{ scale: 0.98 }}>
                <Button
                  fullWidth variant="contained"
                  onClick={handleUpload}
                  disabled={anyBusy || !files.length}
                  startIcon={analyzing
                    ? <CircularProgress size={14} sx={{ color: "#fff" }} />
                    : <UploadFileIcon sx={{ fontSize: "18px !important" }} />}
                  sx={{
                    background: (!analyzing && files.length)
                      ? `linear-gradient(135deg, ${BRAND.navy} 0%, #1a4b9e 100%)`
                      : undefined,
                    bgcolor: (!analyzing && files.length) ? undefined : (isDark ? "#1e2d50" : "#cbd5e1"),
                    color: "#fff",
                    fontWeight: 700, py: 1.5, fontSize: 15,
                    letterSpacing: "0.04em", borderRadius: "12px",
                    boxShadow: (!analyzing && files.length)
                      ? "0 4px 20px rgba(0,53,122,0.4)"
                      : "none",
                    position: "relative", overflow: "hidden",
                    "&:hover": {
                      background: (!analyzing && files.length)
                        ? `linear-gradient(135deg, #1a4b9e 0%, ${BRAND.navy} 100%)`
                        : undefined,
                      transform: "translateY(-1px)",
                      boxShadow: "0 8px 28px rgba(0,53,122,0.45)",
                    },
                    "&:disabled": {
                      color: isDark ? "#475569" : "#94a3b8",
                    },
                    transition: "all 0.22s ease",
                    // Shimmer sweep
                    "@keyframes shimmer": {
                      "0%":   { left: "-80%" },
                      "100%": { left: "140%" },
                    },
                    "&::after": files.length ? {
                      content: '""',
                      position: "absolute",
                      top: 0, bottom: 0,
                      left: "-80%",
                      width: "50%",
                      background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.14), transparent)",
                      transform: "skewX(-20deg)",
                      animation: "shimmer 3s ease-in-out infinite",
                    } : {},
                  }}
                >
                  {analyzing ? phase.label : "Analyze Report"}
                </Button>
              </motion.div>

              {/* Progress bar */}
              <AnimatePresence>
                {analyzing && (
                  <motion.div
                    initial={{ opacity: 0, scaleX: 0.8 }}
                    animate={{ opacity: 1, scaleX: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Box sx={{ mt: 1.5, borderRadius: "6px", overflow: "hidden", height: 5,
                      bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,53,122,0.08)" }}>
                      {/* 2026-10-16 (ui-ux-checker-flagged perf finding) —
                          animates transform:scaleX (compositor-only), not
                          width (triggers layout/paint every frame). */}
                      <motion.div
                        animate={{ scaleX: phase.pct / 100 }}
                        transition={{ duration: 0.7, ease: "easeOut" }}
                        style={{
                          height: "100%", width: "100%", transformOrigin: "left",
                          background: `linear-gradient(to right, ${BRAND.navy}, ${botAccent}, ${BRAND.gold})`,
                          borderRadius: 6,
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        <Box sx={{
                          position: "absolute", inset: 0,
                          background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.25) 50%, transparent 100%)",
                          backgroundSize: "200% 100%",
                          "@keyframes pgShimmer": {
                            "0%":   { backgroundPosition: "-200% 0" },
                            "100%": { backgroundPosition: "200% 0" },
                          },
                          animation: "pgShimmer 1.8s linear infinite",
                        }} />
                      </motion.div>
                    </Box>
                    <Typography sx={{ fontSize: 13.5, color: textSecondary, mt: 0.6, textAlign: "right" }}>
                      {phase.pct}%
                    </Typography>
                  </motion.div>
                )}
              </AnimatePresence>
            </Box>

            {/* Memory actions — now shown for a Proove-typed result too
                (2026-10-15, user-directed correction): "Save to Memory"
                still posts to the EXISTING /add_to_memory/{bot}_bot
                endpoint unchanged (main.py's _save_to_memory is now
                typed-aware server-side), and "Clear Memory" clears the
                typed session state too (see clear_*_bot_memories). */}
            <AnimatePresence>
              {(answer || useMemory) && (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.3 }}
                >
                  <Box sx={{ px: 3, pb: 3, borderTop: `1px solid ${border}`, pt: 2.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                      <Box sx={{ flex: 1, height: "1px", bgcolor: border }} />
                      <Typography sx={{ fontSize: 12.5, fontWeight: 800, letterSpacing: "0.18em",
                        color: textSecondary, textTransform: "uppercase", flexShrink: 0 }}>
                        Memory
                      </Typography>
                      <Box sx={{ flex: 1, height: "1px", bgcolor: border }} />
                    </Box>
                    <Stack spacing={0.75}>
                      {answer && (
                        <Tooltip
                          title={
                            !sessionGrounded
                              ? "A newer report superseded this one — re-upload to enable Save to Memory"
                              : (hasSaved ? "Already saved to memory" : "Save this analysis as context for future trend detection")
                          }
                          placement="right"
                        >
                          <span>
                            <motion.div whileHover={{ scale: hasSaved ? 1 : 1.015 }} whileTap={{ scale: hasSaved ? 1 : 0.97 }}>
                              <Button
                                fullWidth size="small"
                                onClick={handleSaveToMemory}
                                disabled={anyBusy || hasSaved || !sessionGrounded}
                                startIcon={busyAction === "saving"
                                  ? <CircularProgress size={14} sx={{ color: "inherit" }} />
                                  : <SaveIcon sx={{ fontSize: "14px !important" }} />}
                                sx={{
                                  justifyContent: "flex-start", borderRadius: "10px",
                                  bgcolor: hasSaved
                                    ? (isDark ? "rgba(34,197,94,0.1)" : "rgba(34,197,94,0.08)")
                                    : (isDark ? `${botAccent}14` : `${botAccent}0e`),
                                  border: `1px solid ${hasSaved ? "rgba(34,197,94,0.3)" : `${botAccent}3c`}`,
                                  color: hasSaved ? "#22c55e" : botAccent,
                                  fontSize: 14.5, fontWeight: 700,
                                  py: 1, px: 1.5, letterSpacing: "0.02em",
                                  "&:hover": {
                                    bgcolor: hasSaved
                                      ? "rgba(34,197,94,0.14)"
                                      : `${botAccent}22`,
                                    border: `1px solid ${hasSaved ? "rgba(34,197,94,0.45)" : `${botAccent}66`}`,
                                  },
                                  "&:disabled": { opacity: hasSaved ? 0.9 : 0.4 },
                                  transition: "all 0.2s ease",
                                }}
                              >
                                <AnimatePresence mode="wait">
                                  <motion.span
                                    key={hasSaved ? "saved" : "save"}
                                    initial={{ opacity: 0, y: 4 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -4 }}
                                    transition={{ duration: 0.18 }}
                                  >
                                    {hasSaved ? "Saved ✓" : "Save to Memory"}
                                  </motion.span>
                                </AnimatePresence>
                              </Button>
                            </motion.div>
                          </span>
                        </Tooltip>
                      )}
                      {/* 2026-10-16 (ui-ux-checker-flagged real bug) — was
                          gated on (useMemory || isTypedResult) alone, so a
                          generic result saved via "Save to Memory" while
                          useMemory was off hid this button entirely with
                          no way to find it short of toggling an unrelated
                          setting. hasSaved covers that case too now. */}
                      {(useMemory || isTypedResult || hasSaved) && (
                        <motion.div whileHover={{ scale: 1.015 }} whileTap={{ scale: 0.97 }}>
                          <Button
                            fullWidth size="small"
                            onClick={handleClearMemory}
                            disabled={anyBusy}
                            startIcon={busyAction === "clearing"
                              ? <CircularProgress size={14} sx={{ color: "inherit" }} />
                              : <DeleteSweepIcon sx={{ fontSize: "14px !important" }} />}
                            sx={{
                              justifyContent: "flex-start", borderRadius: "10px",
                              bgcolor: isDark ? "rgba(239,68,68,0.07)" : "rgba(239,68,68,0.05)",
                              border: "1px solid rgba(239,68,68,0.18)",
                              color: "#ef4444", fontSize: 14.5, fontWeight: 700,
                              py: 1, px: 1.5, letterSpacing: "0.02em",
                              "&:hover": { bgcolor: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.35)" },
                              "&:disabled": { opacity: 0.4 },
                              transition: "all 0.2s ease",
                            }}
                          >
                            Clear All Memory
                          </Button>
                        </motion.div>
                      )}
                    </Stack>
                    {/* 2026-10-16 (ui-ux-checker-flagged real bug) — this
                        hint used to render for typed results too, but
                        useMemory has no effect on typed Q&A/trends (no
                        Proove-specific chart mapper exists — see
                        isTypedResult's own comment above). */}
                    {!useMemory && answer && !isTypedResult && (
                      <Typography sx={{ fontSize: 13.5, color: textSecondary, mt: 1.25, lineHeight: 1.6 }}>
                        Enable <strong>Long-term memory</strong> so past analyses inform future trend detection.
                      </Typography>
                    )}
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
        </motion.div>
        )}

        {/* ─── MAIN CONTENT ─── */}
        {(!isMobile || mobileView === "results") && (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <AnimatePresence mode="wait">

            {/* Empty state */}
            {!answer && !analyzing && (
              <motion.div
                key="empty"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.4 }}
                style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}
              >
                <Box sx={{
                  flex: 1, overflowY: "auto", display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center", p: 6, textAlign: "center",
                }}>
                  <motion.div
                    animate={{ y: [0, -9, 0] }}
                    transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <Box sx={{
                      width: 96, height: 96,
                      background: isDark
                        ? "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)"
                        : "linear-gradient(135deg, #fff 0%, #eef3ff 100%)",
                      border: `1.5px solid ${border}`,
                      borderRadius: "50%",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      mb: 3.5,
                      boxShadow: isDark
                        ? `0 12px 40px rgba(0,0,0,0.4), 0 0 0 12px rgba(0,53,122,0.06)`
                        : `0 12px 40px rgba(0,53,122,0.1), 0 0 0 12px rgba(0,53,122,0.04)`,
                    }}>
                      <UploadFileIcon sx={{ fontSize: 40, color: BRAND.gold }} />
                    </Box>
                  </motion.div>

                  <Typography sx={{ fontSize: 22, fontWeight: 800, color: textPrimary, mb: 1, letterSpacing: "-0.01em" }}>
                    {isPatient ? "Upload your lab report" : "Upload a patient report"}
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: textSecondary, maxWidth: 380, lineHeight: 1.8 }}>
                    {isPatient
                      ? "Select a PDF or image of your lab results on the left, then click Analyze Report."
                      : "Select the patient's lab report PDF or image on the left. AI will generate a full clinical interpretation."}
                  </Typography>

                  <Box sx={{ display: "flex", gap: 2, mt: 4.5, flexWrap: "wrap", justifyContent: "center" }}>
                    {(isPatient
                      ? ["Plain-language results", "Dietary & lifestyle advice", "Questions for your doctor", "16 languages"]
                      : ["Risk stratification", "Evidence-based treatment", "PubMed citations", "Longitudinal trends"]
                    ).map((f, i) => (
                      <motion.div
                        key={f}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 + i * 0.08, duration: 0.35 }}
                      >
                        <Box sx={{
                          display: "flex", alignItems: "center", gap: 0.75,
                          bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,53,122,0.04)",
                          border: `1px solid ${border}`,
                          borderRadius: "99px", px: 1.75, py: 0.6,
                          backdropFilter: "blur(8px)",
                        }}>
                          <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: BRAND.gold, flexShrink: 0 }} />
                          <Typography sx={{ fontSize: 13.5, color: textSecondary, fontWeight: 500 }}>{f}</Typography>
                        </Box>
                      </motion.div>
                    ))}
                  </Box>
                </Box>
              </motion.div>
            )}

            {/* Loading state */}
            {analyzing && (
              <motion.div
                key="loading"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.28 }}
                style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}
              >
                <Box sx={{
                  flex: 1, overflowY: "auto",
                  display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center", p: 6, textAlign: "center",
                }}>
                  {/* Orbital spinner */}
                  <Box sx={{ position: "relative", width: 96, height: 96, mb: 4 }}>
                    {/* Outer ring */}
                    <Box sx={{
                      position: "absolute", inset: 0,
                      borderRadius: "50%",
                      border: `2px solid ${isDark ? "#1e2d50" : "#e2e8f0"}`,
                    }} />
                    {/* Spinning arc */}
                    <Box sx={{
                      position: "absolute", inset: 0, borderRadius: "50%",
                      border: "2px solid transparent",
                      borderTopColor: BRAND.navy,
                      borderRightColor: `${BRAND.navy}44`,
                      "@keyframes spinArc": { to: { transform: "rotate(360deg)" } },
                      animation: "spinArc 1.1s linear infinite",
                    }} />
                    {/* Counter-rotating arc */}
                    <Box sx={{
                      position: "absolute", inset: 8, borderRadius: "50%",
                      border: "2px solid transparent",
                      borderBottomColor: BRAND.gold,
                      borderLeftColor: `${BRAND.gold}44`,
                      "@keyframes spinArcRev": { to: { transform: "rotate(-360deg)" } },
                      animation: "spinArcRev 1.6s linear infinite",
                    }} />
                    {/* Center pulsing orb */}
                    <Box sx={{
                      position: "absolute",
                      top: "50%", left: "50%",
                      transform: "translate(-50%, -50%)",
                    }}>
                      <motion.div
                        animate={{ scale: [1, 0.78, 1], opacity: [0.9, 0.6, 0.9] }}
                        transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                      >
                        <Box sx={{
                          width: 28, height: 28, borderRadius: "50%",
                          background: `radial-gradient(circle at 35% 35%, ${BRAND.gold}, ${BRAND.gold}88)`,
                          boxShadow: `0 0 16px ${BRAND.gold}66`,
                        }} />
                      </motion.div>
                    </Box>
                  </Box>

                  <AnimatePresence mode="wait">
                    <motion.div
                      key={uploadPhase}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.22 }}
                    >
                      <Typography sx={{ fontSize: 18, fontWeight: 700, color: textPrimary, mb: 0.75, letterSpacing: "-0.01em" }}>
                        {phase.label}
                      </Typography>
                      <Typography sx={{ fontSize: 14.5, color: textSecondary, mb: 4, minHeight: 22 }}>
                        {uploadPhase === "uploading"   && "Converting your document to high-resolution images…"}
                        {uploadPhase === "processing"  && "Detecting report layout and cropping sections…"}
                        {uploadPhase === "analyzing"   && "AI is interpreting every test value and reference range…"}
                        {uploadPhase === "summarizing" && "Formatting your personalised summary…"}
                        {uploadPhase === "loadingStored" && "Fetching your already-analyzed report…"}
                        {!uploadPhase && "Please wait…"}
                      </Typography>
                    </motion.div>
                  </AnimatePresence>

                  {/* Progress track */}
                  <Box sx={{ width: "100%", maxWidth: 440 }}>
                    <Box sx={{
                      height: 6, borderRadius: 99, overflow: "hidden",
                      bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,53,122,0.08)",
                    }}>
                      {/* 2026-10-16 (ui-ux-checker-flagged perf finding) —
                          same compositor-only-transform fix as the sidebar
                          progress bar above. */}
                      <motion.div
                        animate={{ scaleX: phase.pct / 100 }}
                        transition={{ duration: 0.7, ease: "easeOut" }}
                        style={{
                          height: "100%", width: "100%", transformOrigin: "left",
                          background: `linear-gradient(to right, ${BRAND.navy}, ${botAccent}, ${BRAND.gold})`,
                          borderRadius: 99,
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        <Box sx={{
                          position: "absolute", inset: 0,
                          background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)",
                          backgroundSize: "200% 100%",
                          "@keyframes pgShimmer2": {
                            "0%":   { backgroundPosition: "-200% 0" },
                            "100%": { backgroundPosition: "200% 0" },
                          },
                          animation: "pgShimmer2 1.8s linear infinite",
                        }} />
                      </motion.div>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.75 }}>
                      <Typography sx={{ fontSize: 11.5, color: textSecondary }}>{phase.label.replace("…","")}</Typography>
                      <Typography sx={{ fontSize: 11.5, color: textSecondary, fontWeight: 700 }}>{phase.pct}%</Typography>
                    </Box>
                  </Box>
                </Box>
              </motion.div>
            )}

            {/* Analysis result */}
            {answer && !analyzing && (
              <motion.div
                key="result"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.45, ease: "easeOut" }}
                style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}
              >
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                  {/* Result header bar */}
                  <Box sx={{
                    background: `linear-gradient(to right, ${BRAND.navyDark}, ${BRAND.navy})`,
                    px: 3, py: 1.75, flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    gap: 2, flexWrap: "wrap",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
                  }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                      <Box sx={{
                        width: 42, height: 42, borderRadius: "50%", overflow: "hidden", flexShrink: 0,
                        border: "2px solid rgba(192,168,140,0.55)",
                        boxShadow: "0 0 12px rgba(0,0,0,0.4), 0 0 0 1px rgba(192,168,140,0.15)",
                      }}>
                        <img
                          src={isPatient ? "/aiden-avatar.png" : "/claire-avatar.png"}
                          alt={botLabel}
                          style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "50% 12%" }}
                        />
                      </Box>
                      <Box sx={{
                        width: 8, height: 8, borderRadius: "50%", bgcolor: "#22c55e",
                        boxShadow: "0 0 8px rgba(34,197,94,0.7)",
                        "@keyframes resultPulse": { "0%,100%": { boxShadow: "0 0 8px rgba(34,197,94,0.7)" }, "50%": { boxShadow: "0 0 16px rgba(34,197,94,0.4)" } },
                        animation: "resultPulse 2s infinite",
                      }} />
                      <Typography sx={{ fontSize: 15, fontWeight: 800, letterSpacing: "0.1em", color: "rgba(255,255,255,0.95)" }}>
                        {isPatient ? (
                          <><Box component="span" sx={{ color: BRAND.gold, fontWeight: 900 }}>AI</Box>den</>
                        ) : (
                          <>Cl<Box component="span" sx={{ color: BRAND.gold, fontWeight: 900 }}>AI</Box>re</>
                        )}{" · ANALYSIS COMPLETE"}
                      </Typography>
                      <Typography sx={{ fontSize: 14.5, color: "rgba(255,255,255,0.55)" }}>{analysisTimestamp}</Typography>
                      {(() => {
                        const collDate = reportMetadata.find(m =>
                          ["coll. date","collection date","specimen date","collected"].some(k =>
                            m.field?.toLowerCase().replace(/[:.]/g,"").includes(k)
                          )
                        );
                        return collDate && collDate.value && collDate.value !== "Unknown" ? (
                          <Box sx={{
                            fontSize: 13.5, color: "rgba(255,255,255,0.7)",
                            bgcolor: "rgba(255,255,255,0.08)",
                            border: "1px solid rgba(255,255,255,0.1)",
                            px: 1, py: 0.25, borderRadius: "6px",
                            backdropFilter: "blur(4px)",
                          }}>
                            Collected: {collDate.value}
                          </Box>
                        ) : null;
                      })()}
                    </Stack>
                    <Stack direction="row" spacing={0.5}>
                      {[
                        { icon: isSpeaking ? <VolumeOffIcon sx={{ fontSize: 18 }} /> : <VolumeUpIcon sx={{ fontSize: 18 }} />, onClick: handleToggleReadAloud, active: isSpeaking, title: isSpeaking ? "Stop reading" : "Read aloud" },
                        { icon: downloadingPdf ? <CircularProgress size={14} sx={{ color: BRAND.gold }} /> : <DownloadIcon sx={{ fontSize: 18 }} />, onClick: handleDownloadPDF, disabled: downloadingPdf, title: "Download PDF report" },
                        ...(history.length > 1 ? [{ icon: <HistoryIcon sx={{ fontSize: 18 }} />, onClick: () => setShowHistory(v => !v), active: showHistory, title: showHistory ? "Hide history" : "Show session history" }] : []),
                      ].map((btn, i) => (
                        <Tooltip key={i} title={btn.title}>
                          <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                            <IconButton size="small" onClick={btn.onClick} disabled={btn.disabled} sx={{
                              color: btn.active ? BRAND.gold : "rgba(255,255,255,0.45)", p: 1,
                              borderRadius: "8px",
                              border: btn.active ? `1px solid ${BRAND.gold}44` : "1px solid transparent",
                              "&:hover": { color: BRAND.gold, bgcolor: "rgba(255,255,255,0.08)", border: `1px solid ${BRAND.gold}33` },
                              "&:disabled": { opacity: 0.3 },
                              transition: "all 0.18s",
                            }}>
                              {btn.icon}
                            </IconButton>
                          </motion.div>
                        </Tooltip>
                      ))}
                    </Stack>
                  </Box>

                  {/* Session history */}
                  <AnimatePresence>
                    {showHistory && history.length > 1 && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25 }}
                      >
                        <Box sx={{
                          bgcolor: isDark ? "rgba(7,13,31,0.8)" : "rgba(248,250,255,0.9)",
                          backdropFilter: "blur(12px)",
                          borderBottom: `1px solid ${border}`,
                          px: 3, py: 2, maxHeight: 260, overflowY: "auto", flexShrink: 0,
                        }}>
                          <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: textSecondary, letterSpacing: "0.15em", mb: 1.5 }}>
                            SESSION HISTORY
                          </Typography>
                          <Stack spacing={1.5}>
                            {history.map((item, idx) => (
                              // 2026-10-16 (ui-ux-checker-flagged real bug) —
                              // used to label history[0] as "Initial
                              // Analysis" purely by position, regardless of
                              // actual content. After Clear Memory (which
                              // resets history to [] but leaves a generic
                              // result/answer on screen), the next follow-
                              // up becomes history[0] and was mislabeled as
                              // the initial analysis it isn't. Label by what
                              // the entry actually is instead.
                              <Box key={idx} sx={{ borderLeft: `3px solid ${item.type === "Initial Analysis" ? BRAND.gold : border}`, pl: 2 }}>
                                <Typography sx={{ fontSize: 12.5, fontWeight: 700,
                                  color: item.type === "Initial Analysis" ? (isDark ? BRAND.gold : BRAND.navy) : textSecondary, mb: 0.25 }}>
                                  {item.type === "Initial Analysis" ? "Initial Analysis" : `Q: ${item.type}`}
                                </Typography>
                                <Typography sx={{ fontSize: 12.5, color: textSecondary, lineHeight: 1.6,
                                  display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                                  {item.content.replace(/[#*_`]/g, "").slice(0, 200)}…
                                </Typography>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Metadata card */}
                  {reportMetadata.filter(m => m.field && m.value && m.value !== "Unknown").length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.15 }}
                    >
                      <Box sx={{
                        mx: { xs: 3, md: 5 }, mt: 3, mb: 0,
                        border: `1px solid ${border}`,
                        borderLeft: `4px solid ${BRAND.gold}`,
                        borderRadius: "14px",
                        bgcolor: isDark ? "rgba(0,31,77,0.12)" : "rgba(0,53,122,0.025)",
                        overflow: "hidden",
                        backdropFilter: "blur(8px)",
                        boxShadow: isDark
                          ? "0 4px 24px rgba(0,0,0,0.2)"
                          : "0 4px 24px rgba(0,53,122,0.06)",
                        transition: "box-shadow 0.3s",
                        "&:hover": {
                          boxShadow: isDark
                            ? "0 8px 32px rgba(0,0,0,0.3)"
                            : "0 8px 32px rgba(0,53,122,0.1)",
                        },
                      }}>
                        <Box sx={{ px: 2.5, py: 1.25, borderBottom: `1px solid ${border}`,
                          display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: BRAND.gold }} />
                          <Typography sx={{ fontSize: 11.5, fontWeight: 800, letterSpacing: "0.15em",
                            color: isDark ? BRAND.gold : BRAND.navy }}>
                            PATIENT INFORMATION
                          </Typography>
                        </Box>
                        <Box sx={{
                          px: 2.5, py: 2,
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
                          gap: "12px 20px",
                        }}>
                          {reportMetadata
                            .filter(m => m.field && m.value && m.value !== "Unknown")
                            .map((m, i) => (
                              <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 + i * 0.05, duration: 0.3 }}
                              >
                                <Box>
                                  <Typography sx={{ fontSize: 12, fontWeight: 700,
                                    color: textSecondary, letterSpacing: "0.08em",
                                    textTransform: "uppercase", mb: 0.35 }}>
                                    {m.field}
                                  </Typography>
                                  <Typography sx={{ fontSize: 15, fontWeight: 600, color: textPrimary }}>
                                    {m.value}
                                  </Typography>
                                </Box>
                              </motion.div>
                            ))}
                        </Box>
                      </Box>
                    </motion.div>
                  )}

                  {/* Analysis + Q&A thread */}
                  <Box sx={{ flex: 1, overflowY: "auto", px: { xs: 3, md: 5 }, py: 4 }}>
                    <ReactMarkdown
                      components={markdownComponents(isDark, textPrimary, textSecondary, border, BRAND)}
                    >
                      {useAnimation ? typedAnswer : answer}
                    </ReactMarkdown>

                    {/* 2026-10-16 (ui-ux-checker-flagged real bug) — kept
                        OUT of the typewriter-animated `answer` string (used
                        to be concatenated onto its tail, appearing 30+
                        seconds late on a long report with zero warning
                        styling). Rendered immediately, styled like the
                        existing sidebar disclaimer box. */}
                    {typedDisclaimer && (
                      <Box sx={{
                        mt: 2.5, mb: 1, px: 2, py: 1.5,
                        bgcolor: isDark ? "rgba(192,168,140,0.07)" : "rgba(192,168,140,0.1)",
                        border: `1px solid ${isDark ? "rgba(192,168,140,0.18)" : "rgba(192,168,140,0.32)"}`,
                        borderRadius: "10px",
                        display: "flex", alignItems: "flex-start", gap: 1,
                      }}>
                        <Typography sx={{ color: BRAND.gold, fontSize: 15, flexShrink: 0, mt: "1px", lineHeight: 1 }}>⚠</Typography>
                        <Box sx={{ flex: 1 }}>
                          <ReactMarkdown components={markdownComponents(isDark, textSecondary, textSecondary, border, BRAND)}>
                            {typedDisclaimer}
                          </ReactMarkdown>
                        </Box>
                      </Box>
                    )}

                    {followUps.map((item, idx) => (
                      <Box key={idx}>
                        <motion.div
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, mb: 1.5 }}>
                            <Box sx={{
                              background: `linear-gradient(135deg, ${BRAND.navy}, #1a4b9e)`,
                              color: "#fff",
                              px: 2, py: 1.25,
                              borderRadius: "14px 14px 3px 14px",
                              maxWidth: "78%", fontSize: 15, lineHeight: 1.6,
                              boxShadow: "0 4px 16px rgba(0,53,122,0.3)",
                            }}>
                              {item.question}
                            </Box>
                          </Box>
                        </motion.div>
                        <motion.div
                          initial={{ opacity: 0, x: -12 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3, delay: 0.1 }}
                        >
                          <Box sx={{ borderLeft: `3px solid ${botAccent}`, pl: 2.5 }}>
                            <ReactMarkdown
                              components={markdownComponents(isDark, textPrimary, textSecondary, border, BRAND)}
                            >
                              {item.content}
                            </ReactMarkdown>
                          </Box>
                        </motion.div>
                      </Box>
                    ))}

                    {trendsData && trendsData.visit_count >= 2 && (
                      <TrendsPanel
                        charts={trendsData.charts}
                        visitCount={trendsData.visit_count}
                        isDark={isDark}
                        border={border}
                        textPrimary={textPrimary}
                        textSecondary={textSecondary}
                      />
                    )}
                    {trendsError && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                        <Typography sx={{ fontSize: "0.75rem", color: "#c0392b" }}>
                          Couldn't load your visit trends.
                        </Typography>
                        <Button size="small" onClick={fetchTrends} sx={{ textTransform: "none", fontSize: "0.75rem", p: 0, minWidth: 0 }}>Retry</Button>
                      </Box>
                    )}
                  </Box>

                  {/* Sticky follow-up input */}
                  <Box sx={{
                    borderTop: `1px solid ${border}`,
                    px: { xs: 2, md: 4 }, py: 2,
                    bgcolor: isDark ? "rgba(7,13,31,0.9)" : "rgba(255,255,255,0.95)",
                    backdropFilter: "blur(16px)",
                    flexShrink: 0,
                  }}>
                    <Box sx={{
                      display: "flex", alignItems: "flex-end", gap: 1,
                      bgcolor: isDark ? "rgba(15,24,48,0.8)" : "#f4f7ff",
                      border: `1.5px solid ${border}`,
                      borderRadius: "16px", px: 1.75, py: 1,
                      transition: "border-color 0.2s, box-shadow 0.2s",
                      "&:focus-within": {
                        borderColor: botAccent,
                        boxShadow: `0 0 0 4px ${botAccent}18`,
                      },
                    }}>
                      <TextField
                        fullWidth multiline maxRows={4} variant="standard"
                        placeholder={
                          !sessionGrounded
                            ? "A newer report superseded this one — re-upload to ask follow-up questions"
                            : (isTypedResult
                              ? "Ask a follow-up about this Proove report…"
                              : (isPatient
                                ? "Ask a follow-up about your results…"
                                : "Ask a clinical follow-up question…"))
                        }
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleAskQuestion(); } }}
                        disabled={anyBusy || !sessionGrounded}
                        slotProps={{ input: { disableUnderline: true } }}
                        sx={{ "& textarea": { fontSize: 15, color: textPrimary, lineHeight: 1.6 } }}
                      />
                      <motion.div
                        whileHover={(!anyBusy && sessionGrounded && question.trim()) ? { scale: 1.08 } : {}}
                        whileTap={(!anyBusy && sessionGrounded && question.trim()) ? { scale: 0.92 } : {}}
                      >
                        <Box
                          onClick={(!anyBusy && sessionGrounded && question.trim()) ? handleAskQuestion : undefined}
                          sx={{
                            width: 36, height: 36, borderRadius: "11px", flexShrink: 0,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            background: (!anyBusy && sessionGrounded && question.trim())
                              ? `linear-gradient(135deg, ${botAccent}, ${botAccent}cc)`
                              : (isDark ? "#1e2d50" : "#e2e8f0"),
                            color: (!anyBusy && sessionGrounded && question.trim()) ? "#fff" : textSecondary,
                            cursor: (!anyBusy && sessionGrounded && question.trim()) ? "pointer" : "default",
                            boxShadow: (!anyBusy && sessionGrounded && question.trim()) ? `0 4px 16px ${botAccent}44` : "none",
                            transition: "all 0.2s ease",
                          }}>
                          {busyAction === "asking"
                            ? <CircularProgress size={14} sx={{ color: "inherit" }} />
                            : <SendIcon sx={{ fontSize: 15 }} />}
                        </Box>
                      </motion.div>
                    </Box>
                  </Box>
                </Box>
              </motion.div>
            )}

          </AnimatePresence>
        </Box>
        )}
      </Box>

      {/* ── Footer ── */}
      <Box sx={{
        background: `linear-gradient(to right, ${BRAND.navyDark}, #001a4d)`,
        py: 2, px: 3, flexShrink: 0,
        borderTop: "1px solid rgba(255,255,255,0.06)",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center",
          flexWrap: "wrap", gap: 2 }}>
          <Typography sx={{ fontSize: 14.5, color: "rgba(255,255,255,0.35)", letterSpacing: "0.04em" }}>
            © {new Date().getFullYear()} Labarium Diagnostic Systems, Inc. · AI analysis is for informational purposes only.
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Box
              component="a" href="/terms"
              sx={{ fontSize: 14.5, color: "rgba(192,168,140,0.7)", textDecoration: "none",
                "&:hover": { color: BRAND.gold }, cursor: "pointer" }}>
              Terms
            </Box>
            <Box
              component="a" href="/privacy"
              sx={{ fontSize: 14.5, color: "rgba(192,168,140,0.7)", textDecoration: "none",
                "&:hover": { color: BRAND.gold }, cursor: "pointer" }}>
              Privacy
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── Confirm modal ── */}
      <ConfirmModal
        open={confirmClearOpen}
        onConfirm={handleConfirmClear}
        onCancel={() => setConfirmClearOpen(false)}
        isDark={isDark}
      />

      {/* ── Toast notifications ── */}
      <ToastContainer toasts={toasts} onRemove={removeToast} isDark={isDark} />

      {/* ── Hidden printable report ── */}
      <Box sx={{ display: "none" }}>
        <PrintableLabReport
          ref={printRef}
          answer={answer}
          metadata={reportMetadata}
          botLabel={botLabel}
          trendsData={trendsData}
          typedDisclaimer={typedDisclaimer}
        />
      </Box>
    </Box>
    </MotionConfig>
  );
}
